import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { io, Socket } from 'socket.io-client';
import { getIdToken, kickGsiRefresh, onAuthChange } from '../auth/google-auth';
import { blobToBase64 } from '../utils/blobToBase64';
import { WsEvent } from '@voice-assistant/shared';
import type { AskUserQuestionData, ImageAttachment, SessionSummary, ToolActivityMessage, SshTarget, SshStatePayload, SshOutputPayload, ActiveProviderInfo, EffortLevel, MessageReceivedPayload, SessionsReconcilePayload, RequestMissingChunksPayload, CreditBalancePayload, WorkflowProgressEvent, PromptQueuedPayload, SessionIdlePayload, ContextFullness } from '@voice-assistant/shared';
import {
  useContextFullnessStore,
  reconcileFromTurn,
  reconcileFromLoad,
  observeMidTurn,
  clearForSession,
} from '../state/contextFullnessStore';
import { useComposerStore, getIsBusy, getStreamingBufferedText, getStreamGapRecovery, initComposerWire, buildCancelPromptPayload } from '../state/composerStore';
import { reconstructInflightOnLoad } from '../state/inflight-reconstruction';
import { useDocTtsStore } from '../state/docTtsStore';
import { useWaveStore } from '../state/waveStore';
import { useCreditsStore } from '../state/creditsStore';
import { useWorkflowProgressStore } from '../state/workflowProgressStore';
import {
  useSessionListStore,
  useAllSessions,
  useAllSessionMeta,
  needsSessionListSync,
  type SessionEntry,
} from '../state/sessionListStore';
import { useAgentListStore } from '../state/agentListStore';
import {
  SSH_PRIMARY_TERMINAL_ID,
  useSshTerminalStore,
  getSshMode,
  useSshMode,
  useSshTarget,
  useSshOutput,
  useSshOutputEpoch,
  useSshTargets,
  useSshVoiceHistory,
  setTargets as sshStoreSetTargets,
  selectTarget as sshStoreSelectTarget,
  commitConnect as sshStoreCommitConnect,
  appendOutput as sshStoreAppendOutput,
  onDisconnected as sshStoreOnDisconnected,
  exitTerminal as sshStoreExitTerminal,
  appendVoiceHistory as sshStoreAppendVoiceHistory,
  markDetached as sshStoreMarkDetached,
  reconcile as sshStoreReconcile,
} from '../state/sshTerminalStore';
import {
  initSessionLoadWire,
  useSessionLoadStore,
  openSession as slsOpenSession,
  onMessagesLoaded as slsOnMessagesLoaded,
  onOlderLoaded as slsOnOlderLoaded,
  startFetchingInitial as slsStartFetchingInitial,
  beginLoadOlder as slsBeginLoadOlder,
  applyReconcile as slsApplyReconcile,
  setSessionMessages as slsSetMessages,
  setSessionHasMore as slsSetHasMore,
  onSessionSwitched as slsOnSessionSwitched,
  getSessionMessages as slsGetMessages,
} from '../state/sessionLoadStore';
import { initWorkflowControlWire } from '../state/workflowControlWire';
import { patchSessionTuning } from './useSessionTuning';
import { parseSessionHash } from '../utils/sessionUrl';
import { useFileDrawerStore } from '../state/fileDrawerStore';
import { useDrawerStore, getDrawerPanel } from '../state/drawerStore';
import { useSettingsStore } from '../state/settings.store';
import { buildSessionHash } from '../utils/sessionUrl';
import { useCoachStore, shouldGreetPersona, markDiscovered as coachMarkDiscovered, resetFirstRunCoaching, PERSONA_GREETING_ID } from '../state/coachStore';
import { personaGreetingText } from '../utils/personaGreeting';

/**
 * Decode a DB-loaded chat message. For role='agent_question' the persisted
 * `text` is JSON-stringified AskUserQuestionData[] (see voice.gateway.ts
 * onAgentQuestion). Parse it back into `questions` so ChatArea renders the
 * AgentQuestionCard for both live and history-loaded paths. Parse failure
 * falls back to a plain text bubble.
 */
const decodeLoadedMessage = (m: { role: string; text: string }): { role: ChatMessage['role']; text: string; questions?: AskUserQuestionData[] } => {
  if (m.role === 'agent_question') {
    try {
      const parsed = JSON.parse(m.text);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return { role: 'agent_question', text: '', questions: parsed as AskUserQuestionData[] };
      }
    } catch { /* fall through to plain text */ }
  }
  return { role: m.role as ChatMessage['role'], text: m.text };
};

/** Format milliseconds as human-readable duration (e.g., "2h 15m", "45s", "3m 20s") */
const formatElapsed = (ms: number): string => {
  const totalSecs = Math.round(ms / 1000);
  if (totalSecs < 60) return `${totalSecs}s`;
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
};

// blobToBase64 was extracted to ../utils/blobToBase64 in #1622 so the STT path here
// and the memo path (useMemos.ts / App.tsx LogbookModal) share one implementation —
// neither can drift back to a corruption-prone binary emit.

export interface ChatMessage {
  role: 'user' | 'assistant' | 'error' | 'system' | 'system-action' | 'shared' | 'tool' | 'consultation' | 'consultation-injection' | 'cascade-classifier' | 'cascade-tag' | 'agent_question';
  text: string;
  /** Interactive question chips from AskUserQuestion SDK tool (role='agent_question' only) */
  questions?: AskUserQuestionData[];
  streaming?: boolean;
  /** Renderable image srcs for display in chat: base64 data URIs (freshly-sent)
   *  or owner-scoped media URLs built from imagePaths (reloaded). */
  images?: string[];
  /** ALLOWED_ROOT-relative paths to the persisted attachment bytes (Item E). On
   *  reload these are turned into /api/files/chat-image URLs in `images`. */
  imagePaths?: string[];
  /** DB message ID — used as cursor for pagination */
  id?: string;
  /** ISO timestamp — when the message was sent (user) or completed (assistant/CLAUDE_DONE) */
  timestamp?: string;
  /** Model that produced this message (e.g. claude-sonnet-4-6, claude-opus-4-6, gemini-2.5-pro) */
  model?: string;
  /** For consultation messages: the AI provider (opus, gemini, haiku, ollama) */
  provider?: string;
  /** Per-message cost in USD (from token tracking) */
  costUsd?: number;
  /** Input token count (consultation messages only) */
  inputTokens?: number;
  /** Output token count (consultation messages only) */
  outputTokens?: number;
  /** STT correction metadata when the transcript came from Whisper + correction pipeline */
  correction?: {
    provider: 'gemini' | 'regex' | 'none';
    latencyMs: number;
    changed: boolean;
  };
  /** Structured metadata from DB (kind, errorClass, recoveryAction for error messages, etc.) */
  metadata?: Record<string, any>;
}

interface UseSocketOptions {
  /** Defer socket connection until true (e.g. wait for auth) */
  enabled?: boolean;
  onSummaryReady?: (summary: string) => void;
  onChunk?: (chunk: string) => void;
  onDone?: () => void;
  onSessionResumeFailed?: () => void;
  onContextLoaded?: (projects: string[]) => void;
  /** Called when the server auto-switches to a session (voice command or share) */
  onAutoSwitch?: () => void;
  /** Called when the classifier suggests Opus — prompt is held, frontend should enter selection mode */
  onOpusSuggested?: (payload: { sessionId: string; prompt: string; reason: string }) => void;
  /** Max concurrent open tabs (from installation profile). Defaults to 5. */
  maxTabs?: number;
  /** Called when user tries to open a tab but is at the limit */
  onTabLimitReached?: (max: number) => void;
  /** Called when SSH session ends (shell exited) so the app can navigate away from the terminal view */
  onSshDisconnected?: () => void;
  /** Called when the backend refuses to enable browser tools because the user hasn't accepted the consent copy */
  onBrowserConsentRequired?: (payload: { sessionId: string; version: string }) => void;
  /** Called when the user triggers browser intent but their tier doesn't include the browser tool */
  onBrowserTierGateRequired?: (payload: { sessionId: string; reason: string }) => void;
  /**
   * Called when the backend denies a cascade session (FEATURE_GATED `ai.cascade_routing`).
   * The shell bounces the user to the sessions drawer + tier-denial hint. Replaces the old
   * global `cascade-gated` window-event bus (platform-isolation #1657 Phase 2, Slice A):
   * the producer now calls this named handler directly instead of dispatching a global
   * event (R-A2). The handler itself routes through the named `openForCascadeGate()` store
   * action.
   */
  onCascadeGated?: () => void;
}

/** Per-session metadata keyed by session ID */
type SessionMeta = Record<string, { numTurns?: number; totalCostUsd?: number; summary?: string }>;

/** Per-session cached state for background tabs */
interface TabState {
  messages: ChatMessage[];
  hasMore: boolean;
  toolActivities: (ToolActivityMessage & { turnIndex: number })[];
  /** The live turn's persisted tool row (server-supplied id). The live trace above owns its
   *  content, so ChatArea suppresses this row to avoid rendering the same turn twice. */
  inflightToolRowId?: string;
  sessionMeta: { numTurns?: number; totalCostUsd?: number };
  sessionHealth: {
    fileSize?: number;
    lastModified?: number;
    createdAt?: number;
    contextPct?: number;
    inputTokens?: number;
    contextWindow?: number;
    modelsUsed?: { model: string; count: number }[];
    toolCallCount?: number;
    docsUpdateStats?: { count: number; lastAt?: number };
    autoDocsUpdateThreshold?: number;
    autoDocsPaused?: boolean;
    lastHealthAt?: number;
  } | null;
  turnCounter: number;
  /** Active cross-model consultation for this tab (null when idle) */
  consultation: { provider: string; question?: string } | null;
}

const OPEN_TABS_KEY = 'voice-assistant-open-tabs';
const ACTIVE_TAB_KEY = 'voice-assistant-active-tab';
const MAX_TABS = 5;

// saveActiveView/loadActiveView (the ad-hoc `voice-assistant-active-view` key,
// PR #747) moved into state/uiStateStore.ts (`va-ui-state-v1`) — Phase 1 of
// plans/user-ui-preferences-state.md (#1566). The store performs a one-time
// legacy-key migration; no parallel keys remain (R-A4).

function loadOpenTabs(): string[] {
  try {
    const raw = localStorage.getItem(OPEN_TABS_KEY);
    const arr: string[] = raw ? JSON.parse(raw) : [];
    // Deduplicate on load (defensive — prevents stale duplicates from persisted state)
    return [...new Set(arr)];
  } catch { return []; }
}

function saveOpenTabs(tabs: string[]) {
  // Deduplicate before persisting (belt-and-suspenders with loadOpenTabs dedup)
  const unique = [...new Set(tabs)];
  try { localStorage.setItem(OPEN_TABS_KEY, JSON.stringify(unique)); } catch {}
}

function loadActiveTab(): string | undefined {
  try { return localStorage.getItem(ACTIVE_TAB_KEY) ?? undefined; } catch { return undefined; }
}

/**
 * Effective initial session on cold load: a `#s/<id>` URL deep link ALWAYS wins
 * over the persisted active-tab. Without this, the boot-restore loop would load
 * (and activate) the persisted `voice-assistant-active-tab` session even when the
 * user navigated directly to a different session via its deep link, leaving the
 * header stuck on the prior session (regression-followups-2026-06-10 item E).
 * Returns undefined when neither source resolves a session id.
 */
function resolveBootSessionId(): string | undefined {
  const fromHash = parseSessionHash(window.location.hash);
  if (fromHash) return fromHash;
  return loadActiveTab();
}

function saveActiveTab(id: string | undefined) {
  try {
    if (id) localStorage.setItem(ACTIVE_TAB_KEY, id);
    else localStorage.removeItem(ACTIVE_TAB_KEY);
  } catch {}
}

function emptyTabState(): TabState {
  return {
    messages: [],
    // Optimistic default: assume more history may exist until MESSAGES_LOADED
    // (with authoritative hasMore) arrives. Fixes the initial-load race where
    // operator sees first 1-2 messages but cannot scroll up because the
    // "Load older" button is hidden until the fetch completes. Issue #1235.
    hasMore: true,
    toolActivities: [],
    sessionMeta: {},
    sessionHealth: null,
    turnCounter: 0,
    consultation: null,
  };
}

const CURRENT_SESSION_ID_KEY = 'voice-assistant-current-session-id';
function saveCurrentSessionId(id: string | null) {
  if (id) localStorage.setItem(CURRENT_SESSION_ID_KEY, id);
  else localStorage.removeItem(CURRENT_SESSION_ID_KEY);
}
function loadCurrentSessionId(): string | null {
  return localStorage.getItem(CURRENT_SESSION_ID_KEY);
}

/**
 * Route a TOOL_ACTIVITY type:'context' event (Phase 0b occupancy signal) into
 * the contextFullnessStore as a projected overlay. The projected token count
 * arrives pre-computed from the server (anchor + VA-appended chars/4); the
 * overlay is built against the window of the CURRENT settled entry — without
 * one there is nothing honest to project against, so the event is dropped.
 * A fresh mid-turn anchor (contextTokens) becomes a projection riding on
 * itself; the turn-end SESSION_HEALTH_DATA replaces it with the measured union.
 */
function observeContextActivity(sessionId: string, data: ToolActivityMessage): void {
  const cur = useContextFullnessStore.getState().bySession[sessionId];
  const contextWindow = cur && (cur.kind === 'measured' || cur.kind === 'projected') ? cur.contextWindow : undefined;
  if (!contextWindow || contextWindow <= 0) return;
  if (typeof data.projectedContextTokens === 'number' && data.projectedContextTokens > 0) {
    const anchorTokens =
      cur?.kind === 'projected' ? cur.anchorTokens
        : cur?.kind === 'measured' ? cur.contextTokens
          : data.projectedContextTokens;
    observeMidTurn(sessionId, {
      kind: 'projected',
      contextTokens: data.projectedContextTokens,
      contextWindow,
      pct: Math.round((data.projectedContextTokens / contextWindow) * 100),
      anchorTokens,
    });
  } else if (
    typeof data.contextTokens === 'number' && data.contextTokens > 0 &&
    data.contextTokensSource !== 'stale-anchor' && data.contextTokensSource !== 'unmetered'
  ) {
    observeMidTurn(sessionId, {
      kind: 'projected',
      contextTokens: data.contextTokens,
      contextWindow,
      pct: Math.round((data.contextTokens / contextWindow) * 100),
      anchorTokens: data.contextTokens,
    });
  }
}

export function useSocket(options?: UseSocketOptions) {
  const [isHandshakeComplete, setIsHandshakeComplete] = useState(false);
  const isHandshakeCompleteRef = useRef(false);
  useEffect(() => { isHandshakeCompleteRef.current = isHandshakeComplete; }, [isHandshakeComplete]);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [summarizing, setSummarizing] = useState(false);
  // Phase 4: `sessions` + `sessionMeta` are derived from sessionListStore via
  // useShallow-stable hooks; useSocket no longer owns these as `useState`.
  // Per-site cut-over below writes directly to the store; the consumer surface
  // (useSocket return) is unchanged.
  const sessions = useAllSessions();
  const sessionMeta = useAllSessionMeta();
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(loadCurrentSessionId);
  const [toolActivities, setToolActivities] = useState<(ToolActivityMessage & { turnIndex: number })[]>([]);
  /** The live turn's persisted tool row for the ACTIVE tab — ChatArea suppresses it (the live
   *  trace, seeded from it, owns that turn's content). */
  const [inflightToolRowId, setInflightToolRowIdState] = useState<string | undefined>(undefined);
  const [usage, setUsage] = useState<{ turnsPct: number; costPct: number; turns: number; maxTurns: number; costUsd: number; maxCostUsd: number } | null>(null);
  // ssh-terminal-fsm-migration Phase 2: the sshTerminalStore is now AUTHORITATIVE.
  // These are derived store selectors (not useState) — the legacy setters are gone;
  // the SSH_* handlers below write the store directly via named actions. Consumers
  // (App.tsx + the SSH components) read these exactly as before, now store-backed.
  const sshMode = useSshMode(SSH_PRIMARY_TERMINAL_ID);
  const sshConnected = sshMode === 'connected';
  const sshTarget = useSshTarget(SSH_PRIMARY_TERMINAL_ID);
  const sshOutput = useSshOutput(SSH_PRIMARY_TERMINAL_ID);
  const sshOutputEpoch = useSshOutputEpoch(SSH_PRIMARY_TERMINAL_ID);
  const sshTargets = useSshTargets();
  const sshVoiceHistory = useSshVoiceHistory(SSH_PRIMARY_TERMINAL_ID);
  const [deployStatus, setDeployStatus] = useState<'normal' | 'justDeployed' | 'rebuildScheduled'>('normal');
  // Detail for a pending rebuild (deferral count + first-requested) so the
  // brand-signal tooltip can show "deferred N×" — FU-2, #1369.
  const [deployPending, setDeployPending] = useState<{ deferCount: number; firstRequestedAt: string | null } | null>(null);

  // ── session-list FSM (Phase 4 — legacy state deleted) ──────────────────────
  // Per-site WsEvent handlers below now call `useSessionListStore.getState()`
  // actions directly (reconcile / upsert / rename / remove / patch / setSummary
  // / setMeta). The Phase 2 mirror useEffect is gone — the store IS the wire.
  const [lastDeploy, setLastDeploy] = useState<{ completedAt?: string; requestedBy?: string; status?: string } | null>(null);
  const [sessionHealth, setSessionHealth] = useState<TabState['sessionHealth']>(null);
  // User-level threshold — same for all sessions; updated the first time any SESSION_HEALTH_DATA
  // carries it. Avoids the bug where swapping sessions resets the tier calculation to the default.
  const [userAutoDocsThreshold, setUserAutoDocsThreshold] = useState<number>(71);

  // Provider failover state
  const [providerInfo, setProviderInfo] = useState<ActiveProviderInfo | null>(null);
  // Per-session provider error state (set on PROVIDER_ERROR, cleared on next send or session switch).
  // authRequired/remedy mark an Anthropic auth/credit failure → guidance card (#1057).
  const [providerErrors, setProviderErrors] = useState<Map<string, { provider: string; message: string; offerFallback: boolean; authRequired?: boolean; remedy?: 'claude_login' }>>(new Map());

  // Live cross-model consultation indicator — which provider is currently thinking
  const [activeConsultation, setActiveConsultation] = useState<{ provider: string; question?: string } | null>(null);

  const [hasMore, setHasMore] = useState(true);
  // Desktop-home rail (issue #2201): true after the first SESSIONS_LIST reconcile — the
  // renderers' boundary between "still loading" and "the list is genuinely empty".
  const sessionsSynced = useSessionListStore((s) => s.synced);

  // Multi-session tab state
  const [openTabs, setOpenTabs] = useState<string[]>(loadOpenTabs);
  const activeTabIdRef = useRef<string | undefined>(loadActiveTab());

  /** Persists currentSessionId to localStorage on every change. */
  const setAndPersistCurrentSessionId = useCallback((id: string | null) => {
    saveCurrentSessionId(id);
    setCurrentSessionId(id);
  }, []);

  // Validate persisted currentSessionId once openTabs is first populated.
  // Guards against stale IDs (session deleted since last visit).
  //
  // Deep-link precedence (regression-followups-2026-06-10 item E): when the URL
  // is a `#s/<id>` deep link, that session legitimately may NOT be in the
  // persisted openTabs (it was opened directly, not from a saved tab). Do NOT
  // overwrite it with openTabs[0] in that case — the deep link wins, and the
  // server's authoritative SESSIONS_LIST reconcile (below) handles the case
  // where the deep-linked session is actually dead/stale.
  const hasValidatedSessionId = useRef(false);
  useEffect(() => {
    if (hasValidatedSessionId.current || openTabs.length === 0) return;
    hasValidatedSessionId.current = true;
    if (parseSessionHash(window.location.hash)) return; // deep link wins — don't clobber
    const persisted = loadCurrentSessionId();
    if (persisted && !openTabs.includes(persisted)) {
      setAndPersistCurrentSessionId(openTabs[0] ?? null);
    }
  }, [openTabs, setAndPersistCurrentSessionId]);

  /** Per-session state cache (includes active tab — always in sync) */
  const tabCache = useRef<Map<string, TabState>>(new Map());

  // Latest targets, so the `sshConnect(targetId)` callback (stable deps) can
  // resolve the full SshTarget for the FSM-store selectTarget action.
  const sshTargetsRef = useRef<SshTarget[]>([]);
  /** Current STT language — read by sendAudio via ref to avoid callback recreation.
   *  settings-zustand-migration Phase 4: sourced from useSettingsStore. The
   *  internal ref shape is preserved because the audio-send callbacks rely on
   *  reading the latest value at fire time without re-creating themselves.
   *  A useEffect below subscribes to store changes and keeps the ref in sync. */
  const sttLanguageRef = useRef<string>(useSettingsStore.getState().sttLanguage);
  const socketRef = useRef<Socket | null>(null);
  const pendingImagesRef = useRef<string[]>([]);
  /** When set, next TRANSCRIPTION event populates input instead of chat */
  const transcribeOnlyCallbackRef = useRef<((text: string) => void) | null>(null);
  /** Timeout guard — clears whisperTranscribing if Whisper never responds */
  const transcribeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Phase 5: debounce timer for PREFERENCES_UPDATED → loadFromBackend coalescing */
  const preferencesReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Last Redis Stream event ID — for replay on reconnect */
  const lastEventIdRef = useRef<string>(
    (() => { try { return localStorage.getItem('voice-assistant-last-event-id') || '0-0'; } catch { return '0-0'; } })()
  );
  /** Set of Redis event IDs already processed — prevents duplicate delivery */
  const processedEventIds = useRef<Set<string>>(new Set());
  /** Pending prompt to auto-send when a new session becomes active (e.g. plan review) */
  const pendingPromptRef = useRef<string | null>(null);
  /** Flag: true after newSession() is called, cleared when MESSAGES_LOADED fires */
  const pendingNewSession = useRef(false);
  /** Persona-greeting gate (coach-tooltip-system.md §5.1) captured at newSession()
   *  call time (sessionCountBeforeCreate is only meaningful BEFORE the new session
   *  exists), consumed once by MESSAGES_LOADED's empty-session branch below. */
  const pendingPersonaGreetingRef = useRef(false);

  /** rAF handle for batched streaming flush — one per session to avoid stacking */
  const chunkFlushRafRef = useRef<number | null>(null);
  /** Set of session IDs with pending chunk data to flush */
  const pendingChunkFlushRef = useRef<Set<string>>(new Set());
  /** Sessions currently in an auto-continuation turn — tracked so the isolated
   * CLAUDE_DONE listener below can clear the orchestration gate (#1247). */
  const autoContinuingRef = useRef<Set<string>>(new Set());
  /** Deduplicates rapid cancel taps; also read in socket event handlers (must be declared early) */
  const cancelPendingRef = useRef(false);

  /** Track the latest Redis event ID from any event payload.
   *  Called on every event that carries `_lastEventId` so that reconnects
   *  replay only unseen events — not just from the last CLAUDE_DONE. */
  const trackEventId = (data: any) => {
    if (data?._lastEventId && data._lastEventId > lastEventIdRef.current) {
      lastEventIdRef.current = data._lastEventId;
      try { localStorage.setItem('voice-assistant-last-event-id', data._lastEventId); } catch {}
    }
  };
  const summaryCallbackRef = useRef(options?.onSummaryReady);
  summaryCallbackRef.current = options?.onSummaryReady;
  const chunkCallbackRef = useRef(options?.onChunk);
  chunkCallbackRef.current = options?.onChunk;
  const doneCallbackRef = useRef(options?.onDone);
  doneCallbackRef.current = options?.onDone;
  const resumeFailedRef = useRef(options?.onSessionResumeFailed);
  resumeFailedRef.current = options?.onSessionResumeFailed;
  const contextLoadedRef = useRef(options?.onContextLoaded);
  contextLoadedRef.current = options?.onContextLoaded;
  const autoSwitchRef = useRef(options?.onAutoSwitch);
  autoSwitchRef.current = options?.onAutoSwitch;
  const opusSuggestedRef = useRef(options?.onOpusSuggested);
  opusSuggestedRef.current = options?.onOpusSuggested;
  const sshDisconnectedRef = useRef(options?.onSshDisconnected);
  sshDisconnectedRef.current = options?.onSshDisconnected;
  const browserConsentRequiredRef = useRef(options?.onBrowserConsentRequired);
  browserConsentRequiredRef.current = options?.onBrowserConsentRequired;
  const browserTierGateRequiredRef = useRef(options?.onBrowserTierGateRequired);
  browserTierGateRequiredRef.current = options?.onBrowserTierGateRequired;
  const cascadeGatedRef = useRef(options?.onCascadeGated);
  cascadeGatedRef.current = options?.onCascadeGated;

  /** Map<clientMsgId, timerId> — 5 s no-ack timers; cleared on MESSAGE_RECEIVED. */
  const noAckTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /**
   * Holds the clientMsgId for a voice send (AUDIO_END) while Whisper is transcribing.
   * When TRANSCRIPTION arrives (non-transcribeOnly), we call commitSend with the transcribed text.
   * Cleared on each TRANSCRIPTION event.
   */
  const pendingVoiceClientMsgId = useRef<{ clientMsgId: string; sessionId: string } | null>(null);

  // ── Tab cache helpers ──────────────────────────────────────────────

  const isActiveTab = (sessionId: string | undefined): boolean =>
    !!sessionId && sessionId === activeTabIdRef.current;

  /** Get or create a tab's cached state */
  const getTab = (sessionId: string): TabState => {
    let tab = tabCache.current.get(sessionId);
    if (!tab) {
      tab = emptyTabState();
      tabCache.current.set(sessionId, tab);
    }
    return tab;
  };

  /** Update a tab's cached messages. If it's the active tab, also update React state. */
  const setTabMessages = (sessionId: string | undefined, updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    if (!sessionId) { setMessages(updater); return; }
    const tab = getTab(sessionId);
    tab.messages = updater(tab.messages);
    if (isActiveTab(sessionId)) setMessages(tab.messages);
    // Phase 5: upsert into sessionLoadStore — single source of truth for ChatArea.
    // Was Phase 3 dual-write (entries existed only after onMessagesLoaded); Phase 5
    // promotes the store to canonical so streaming chunks / SESSION_SWITCHED /
    // direct mutations are all visible to ChatArea's useSessionMessages subscriber.
    slsSetMessages(sessionId, tab.messages);
  };

  const setTabToolActivities = (sessionId: string | undefined, updater: (prev: (ToolActivityMessage & { turnIndex: number })[]) => (ToolActivityMessage & { turnIndex: number })[]) => {
    if (!sessionId) { setToolActivities(updater); return; }
    const tab = getTab(sessionId);
    tab.toolActivities = updater(tab.toolActivities);
    if (isActiveTab(sessionId)) setToolActivities(tab.toolActivities);
  };

  const setTabInflightToolRowId = (sessionId: string | undefined, rowId: string | undefined) => {
    if (!sessionId) { setInflightToolRowIdState(rowId); return; }
    const tab = getTab(sessionId);
    tab.inflightToolRowId = rowId;
    if (isActiveTab(sessionId)) setInflightToolRowIdState(rowId);
  };

  const setTabSessionMeta = (sessionId: string | undefined, meta: { numTurns?: number; totalCostUsd?: number }) => {
    if (!sessionId) return;
    const tab = getTab(sessionId);
    tab.sessionMeta = meta;
    if (isActiveTab(sessionId)) useSessionListStore.getState().setMeta(sessionId, meta);
  };

  const setTabSessionHealth = (sessionId: string | undefined, health: Partial<NonNullable<TabState['sessionHealth']>>) => {
    if (!sessionId) { setSessionHealth(prev => prev ? { ...prev, ...health } : health as TabState['sessionHealth']); return; }
    const tab = getTab(sessionId);
    tab.sessionHealth = tab.sessionHealth ? { ...tab.sessionHealth, ...health } : health as TabState['sessionHealth'];
    if (isActiveTab(sessionId)) setSessionHealth(tab.sessionHealth);
  };

  const setTabHasMore = (sessionId: string | undefined, value: boolean) => {
    if (!sessionId) { setHasMore(value); return; }
    const tab = getTab(sessionId);
    tab.hasMore = value;
    if (isActiveTab(sessionId)) setHasMore(value);
    // Phase 5: upsert into sessionLoadStore (was Phase 3 update-only).
    slsSetHasMore(sessionId, value);
  };

  const setTabConsultation = (sessionId: string | undefined, value: TabState['consultation']) => {
    if (!sessionId) { setActiveConsultation(value); return; }
    const tab = getTab(sessionId);
    tab.consultation = value;
    if (isActiveTab(sessionId)) setActiveConsultation(value);
  };

  /** Schedule a batched React state update for streaming messages.
   *  Chunks accumulate in the composer machine's `bufferedText` (synchronous via
   *  onStreamChunk), but React re-renders are coalesced to at most once per
   *  animation frame (~60fps). Reduces renders from 50-100/sec to ~16/sec. */
  const scheduleChunkFlush = (sessionId: string, model?: string) => {
    pendingChunkFlushRef.current.add(sessionId);
    if (chunkFlushRafRef.current != null) return; // already scheduled
    chunkFlushRafRef.current = requestAnimationFrame(() => {
      chunkFlushRafRef.current = null;
      const pending = new Set(pendingChunkFlushRef.current);
      pendingChunkFlushRef.current.clear();
      for (const sid of pending) {
        const tab = tabCache.current.get(sid);
        if (!tab) continue;
        // The machine owns the in-flight streaming text (bufferedText on the
        // streaming outbound entry); the flush materialises it into the message.
        const buf = getStreamingBufferedText(useComposerStore.getState(), sid);
        if (!buf) continue;
        // Read model from the tab's last streaming message or use provided model
        const lastMsg = tab.messages[tab.messages.length - 1];
        const msgModel = lastMsg?.streaming ? (model || lastMsg.model) : model;
        if (lastMsg?.streaming) {
          tab.messages = [
            ...tab.messages.slice(0, -1),
            { role: 'assistant', text: buf, streaming: true, model: msgModel || lastMsg.model },
          ];
        } else {
          tab.messages = [
            ...tab.messages,
            { role: 'assistant', text: buf, streaming: true, model: msgModel },
          ];
        }
        if (isActiveTab(sid)) setMessages(tab.messages);
        // Phase 5: dispatch streaming update to sessionLoadStore so ChatArea
        // (subscribed via useSessionMessages) re-renders on each chunk frame.
        slsSetMessages(sid, tab.messages);
      }
    });
  };

  /** Immediately flush any pending chunk data to React state (called on CLAUDE_DONE). */
  const flushChunksNow = (sessionId: string) => {
    pendingChunkFlushRef.current.delete(sessionId);
    const tab = tabCache.current.get(sessionId);
    if (!tab) return;
    // The machine owns the in-flight streaming text (bufferedText on the
    // streaming outbound entry).
    const buf = getStreamingBufferedText(useComposerStore.getState(), sessionId);
    if (!buf) return;
    const lastMsg = tab.messages[tab.messages.length - 1];
    if (lastMsg?.streaming) {
      tab.messages = [
        ...tab.messages.slice(0, -1),
        { role: 'assistant', text: buf, streaming: true, model: lastMsg.model },
      ];
    } else {
      tab.messages = [...tab.messages, { role: 'assistant', text: buf, streaming: true }];
    }
    if (isActiveTab(sessionId)) setMessages(tab.messages);
    // Phase 5: dispatch terminal flush to sessionLoadStore.
    slsSetMessages(sessionId, tab.messages);
  };

  const getTurnCounter = (sessionId: string | undefined): number => {
    if (!sessionId) return 0;
    return getTab(sessionId).turnCounter;
  };

  const incTurnCounter = (sessionId: string | undefined) => {
    if (!sessionId) return;
    getTab(sessionId).turnCounter++;
  };

  // ── Sync active tab state to React ─────────────────────────────────

  /** Load a tab's cached state into React state (used on tab switch) */
  const syncTabToState = useCallback((sessionId: string) => {
    const tab = getTab(sessionId);
    setMessages(tab.messages);
    setHasMore(tab.hasMore);
    setToolActivities(tab.toolActivities);
    setInflightToolRowIdState(tab.inflightToolRowId);
    useSessionListStore.getState().setMeta(sessionId, tab.sessionMeta);
    setSessionHealth(tab.sessionHealth);
    setActiveConsultation(tab.consultation);
  }, []);

  // Initialize session-load store wire on every render (idempotent: all callbacks
  // close over stable refs so repeated calls have no observable side effect).
  initSessionLoadWire({
    socketEmit: (event, data) => socketRef.current?.emit(event as any, data),
    setActiveTab: (sid) => {
      activeTabIdRef.current = sid;
      saveActiveTab(sid);
      setAndPersistCurrentSessionId(sid);
    },
    hasCachedMessages: (sid) => {
      const tab = tabCache.current.get(sid);
      return !!(tab && tab.messages.length > 0);
    },
    syncLegacyTabToState: syncTabToState,
  });

  // Agent Runs controls (SLSWire precedent above): bind the workflowControlWire emit bridge
  // so AgentRunsPanel's Kill/Dismiss reach the backend @SubscribeMessage handlers. Idempotent —
  // the callback closes over the stable socketRef.
  initWorkflowControlWire((event, data) => socketRef.current?.emit(event as any, data));

  // Composer stream-stall watchdog (CS-1, plan §9.3): the module-level watchdog in
  // composerStore emits WsEvent.CANCEL_PROMPT for a silence-stalled session through this
  // bridge — per-session (NOT the active-tab-only `cancelPrompt` callback), so it can abort
  // a stalled BACKGROUND session's turn. Idempotent bind (closes over the stable socketRef).
  initComposerWire({
    cancelPrompt: (sid) => socketRef.current?.emit(WsEvent.CANCEL_PROMPT, sid ? { sessionId: sid } : undefined),
  });

  // ── Usage ──────────────────────────────────────────────────────────

  const fetchUsage = useCallback(() => {
    fetch('/api/usage')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setUsage(data); })
      .catch(() => {});
  }, []);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) return;

    // Credits origin binding (valko-credits-platform Phase 2): the creditsStore wallet
    // is keyed by the authenticated user identity (email — the workspace ACL key). Bind
    // via the named action whenever auth resolves; a CREDIT_BALANCE that lands before
    // the bind is buffered inside the store and applied on bind (never dropped, never
    // rendered without an origin). Sign-out is not unbound here — the socket teardown
    // below covers it.
    const unsubscribeCreditsBind = onAuthChange((user) => {
      if (user?.email) useCreditsStore.getState().bindUser(user.email);
    });

    const socket = io({
      withCredentials: true,
      transports: ['websocket'],
      // Send GSI token in Socket.IO handshake for backend verification.
      auth: async (cb) => {
        try {
          const token = await getIdToken();
          if (!token) {
            // Token expired — kick a silent GSI refresh so the NEXT reconnect
            // gets a fresh Bearer token. The backend cookie fallback keeps this
            // reconnect authenticated via the 7-day va_session cookie.
            kickGsiRefresh();
          }
          cb(token ? { token } : {});
        } catch {
          cb({});
        }
      },
    });

    // Emits issued before the ACP capability handshake (#1464) completes are
    // QUEUED, not dropped. The boot-restore loop below + any server-event-driven
    // emit (e.g. SESSIONS_RECONCILE → applyReconcile → LOAD_TAB_MESSAGES) fire
    // synchronously inside the `connect` handler — BEFORE the async INITIALIZE
    // ack flips isHandshakeComplete. Silently dropping them stranded every
    // boot-restored session in `fetching_initial` until the 10 s fetch-timer
    // flipped it to `error` ("Could not load messages") — the A2 cold-load bug
    // (independent of the #1532 casing fix; emits never reached the backend at
    // all). Buffer them in temporal order and flush once the gate opens.
    const pendingEmits: Array<{ event: string; args: any[] }> = [];
    const originalEmit = socket.emit.bind(socket);
    socket.emit = (event: string, ...args: any[]) => {
      if (!isHandshakeCompleteRef.current && event !== WsEvent.INITIALIZE) {
        // Don't queue ack-callback emits — a queued emit carrying a callback
        // would leave the caller awaiting an ack that fires much later (or with
        // a stale closure). Boot-restore emits are fire-and-forget (no ack arg).
        const hasAckCallback = args.length > 0 && typeof args[args.length - 1] === 'function';
        if (hasAckCallback) {
          console.warn(`[useSocket] Dropped ack-bearing message ${event} issued before handshake`);
          return socket as any;
        }
        pendingEmits.push({ event, args });
        return socket as any;
      }
      return originalEmit(event, ...args);
    };
    const flushPendingEmits = () => {
      // Drain in FIFO order; isHandshakeCompleteRef is already true so these
      // pass straight through originalEmit.
      const queued = pendingEmits.splice(0, pendingEmits.length);
      for (const { event, args } of queued) originalEmit(event, ...args);
    };

    socket.on('connect', () => {
      socket.emit(WsEvent.INITIALIZE, { audioFormat: 'pcm', richText: true }, () => {
        isHandshakeCompleteRef.current = true; // open the gate synchronously so flushed emits pass
        setIsHandshakeComplete(true);
        flushPendingEmits(); // deliver boot-restore + reconcile emits queued during the handshake
      });
      setConnected(true);
      // Wire socket emit into docTtsStore so it can request synthesis
      useDocTtsStore.getState().setEmit((event, payload) => socket.emit(event as string, payload));
      // Invalidate model cache on every (re)connect — backend restart after deploy
      // may have added new models, and module-level cache never self-expires.
      import('./useModels').then(({ invalidateModelCache }) => invalidateModelCache()).catch(() => {});

      // Agent Runs self-heal, pull half (plan §1): request the authoritative run snapshot on
      // EVERY (re)connect. A run_finished published while this client was disconnected — or
      // before the socket bound ("durable only", workflow-emitter) — is otherwise never
      // recovered and leaves a zombie "running" card until some unrelated push arrives.
      // Runs before the `recovered` early-return on purpose: the pull is cheap + idempotent
      // (reconcileSnapshot is server-authoritative), so both paths are covered.
      socket.emit(WsEvent.WORKFLOW_REQUEST_SNAPSHOT, {});

      // Reconcile the pending-rebuild indicator against server truth on (re)connect
      // — a client that connects mid-deferral should show the amber dot immediately,
      // not wait for the next ~8s defer emit (FU-2, #1369).
      fetch('/api/rebuild')
        .then((r) => (r.ok ? r.json() : null))
        .then((s: any) => {
          if (s && s.scheduled) {
            setDeployStatus('rebuildScheduled');
            setDeployPending({ deferCount: s.deferCount ?? 0, firstRequestedAt: s.firstRequestedAt ?? null });
          } else {
            setDeployPending(null);
          }
        })
        .catch(() => {});

      if ((socket as any).recovered) {
        fetchUsage();
        return;
      }

      // Restore the active session by ID. A `#s/<id>` URL deep link ALWAYS wins
      // over the persisted active-tab (regression-followups-2026-06-10 item E) —
      // resolveBootSessionId() encodes that precedence. SWITCH_SESSION loads
      // messages from DB by session ID and works correctly for relay sessions.
      // The generic LOAD_MESSAGES uses getOrCreateActiveSession on the backend
      // which has no memory of which session was last viewed and can return the
      // wrong session (especially if the last viewed session was a relay).
      const bootSessionId = resolveBootSessionId();
      if (bootSessionId) {
        // Synchronously make the resolved session the active one so early reads
        // (health-check loop below, `data.sessionId || activeTabIdRef.current`
        // fallbacks) target it before the SWITCH_SESSION reply lands. The reply
        // re-confirms via onSessionSwitched → setActiveTab.
        activeTabIdRef.current = bootSessionId;
        saveActiveTab(bootSessionId);
        // A deep-linked session may not be in the persisted open-tabs list — add
        // it so it becomes a real tab and the authoritative SESSIONS_LIST prune
        // (which removes ids the server doesn't know) governs its staleness.
        setOpenTabs((prev) => {
          if (prev.includes(bootSessionId)) return prev;
          const next = [...prev, bootSessionId];
          saveOpenTabs(next);
          return next;
        });
        // Use SWITCH_SESSION — it loads messages by session ID from DB regardless
        // of what the backend considers the "active" session in memory.
        slsStartFetchingInitial(bootSessionId);
        socket.emit(WsEvent.SWITCH_SESSION, { sessionId: bootSessionId });
      } else {
        // No saved session — fall back to backend's getOrCreateActiveSession
        socket.emit(WsEvent.LOAD_MESSAGES);
      }
      socket.emit(WsEvent.LIST_SESSIONS);
      fetchUsage();

      // Load messages for all other open tabs (warm them so swiping back is
      // instant). Skip the boot session — its SWITCH_SESSION above already
      // initiated the fetch.
      const savedTabs = loadOpenTabs();
      for (const tabId of savedTabs) {
        if (tabId === bootSessionId) continue;
        slsStartFetchingInitial(tabId);
        socket.emit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: tabId });
      }

      // Always request replay — even with '0-0' (first session ever).
      // Redis XRANGE from '0-0' returns all stored events (up to 2000).
      // This ensures streaming chunks are restored after page refresh.
      socket.emit(WsEvent.REPLAY_EVENTS, { lastEventId: lastEventIdRef.current });

      // Immediate health check for any tabs that were busy before disconnect.
      // Without this, a tab that was busy when the socket dropped will stay in
      // "busy" state for up to 5 minutes before the stuck-session guard fires.
      // This gives instant feedback: if the query finished → reload messages,
      // if still alive → resume streaming.
      setTimeout(() => {
        const csState = useComposerStore.getState();
        for (const [sid] of tabCache.current) {
          if (getIsBusy(csState, sid)) {
            socket.emit(WsEvent.CHECK_QUERY_HEALTH, { sessionId: sid });
          }
        }
      }, 500); // Small delay to let LOAD_TAB_MESSAGES / REPLAY_EVENTS arrive first
    });
    socket.on(WsEvent.DEPLOY_STATUS, (data: any) => {
      if (data?.justDeployed) {
        setDeployStatus('justDeployed');
        setTimeout(() => setDeployStatus('normal'), 8000);
      } else if (data?.rebuildQueue) {
        const rq = data.rebuildQueue;
        setDeployStatus(rq.scheduled ? 'rebuildScheduled' : 'normal');
        setDeployPending(
          rq.scheduled
            ? { deferCount: rq.deferCount ?? 0, firstRequestedAt: rq.firstRequestedAt ?? null }
            : null,
        );
      }
      if (data?.lastDeploy) {
        setLastDeploy(data.lastDeploy);
      }
    });
    socket.on('disconnect', () => {
      setConnected(false);
      setDeployStatus('normal');
      setDeployPending(null);
      // Drop any emits still queued behind an unfinished handshake — they are
      // stale for the next connection (the reconnect's own boot-restore loop
      // re-issues fresh ones). Prevents replaying a half-built batch.
      pendingEmits.length = 0;
      // ssh-terminal-fsm Phase 3: a live SSH terminal is now DETACHED, not gone —
      // the host PTY stays alive server-side. Mark it detached (no-op unless
      // connected) and wait for the typed SSH_STATE on reconnect to reconcile it
      // (connected/idle). NEVER guess the PTY is dead from a frontend timeout.
      sshStoreMarkDetached(SSH_PRIMARY_TERMINAL_ID);
      // Do NOT clear busy/streaming state here — queries continue running on the
      // backend even after socket disconnect. On reconnect, the backend reattaches
      // running queries and replays Redis Stream events, restoring the streaming
      // state. Clearing the streaming entry here would flash empty content before
      // replay rebuilds it; clearing busy would briefly show "idle" mid-work.
      // The stuck-session guard in App.tsx (60s poll) still protects
      // against truly orphaned busy states.
      setSummarizing(false);
      // Disconnect mid-transcription → clear the callback so InputBar exits
      // "Transcribing…" state. Without this, a network drop leaves the mic pill
      // frozen forever and the cancel button has no effect.
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
        transcribeTimeoutRef.current = null;
      }
      if (transcribeOnlyCallbackRef.current) {
        const cb = transcribeOnlyCallbackRef.current;
        transcribeOnlyCallbackRef.current = null;
        cb('');
      }
    });

    // ── Session-aware event handlers ─────────────────────────────────

    socket.on(WsEvent.QUERY_IN_PROGRESS, (data: { sessionId?: string; startedAt: number; elapsed: number }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (sid) {
        // Re-assert composer busy for a query that was already in flight when this
        // client (re)connected — a non-local producer with no commitSend (#1247
        // reconnect-resume). Without this the composer reads idle while the agent is
        // still working, and the SESSIONS_RECONCILE getIsBusy guard skips the reconnect
        // message reload. markServerBusy uses a synthetic streaming entry (NOT
        // orchestrationGated) so the input bar stays usable; released by
        // onStreamDone/onPipelineError on any terminal (CLAUDE_DONE, ERROR, cancel,
        // health-not-alive, reconcile-not-active).
        useComposerStore.getState().markServerBusy(sid);
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: `⏳ Claude is still working (started ${formatElapsed(data.elapsed)} ago). Streaming will resume here...` },
        ]);
      }
    });

    socket.on(WsEvent.QUERY_HEALTH_STATUS, (data: { sessionId: string; alive: boolean; elapsed?: number }) => {
      const sid = data.sessionId;
      const tab = getTab(sid);
      if (data.alive) {
        setTabMessages(sid, (prev) => {
          // Replace any existing health-check message instead of stacking
          const filtered = prev.filter((m) => !(m.role === 'system' && m.text.startsWith('⏳ Agent working')));
          return [...filtered, { role: 'system', text: `⏳ Agent working for ${formatElapsed(data.elapsed || 0)} with no output — still active on backend.` }];
        });
      } else {
        // Query is NOT running — we missed CLAUDE_DONE. Clear busy + reload messages.
        useComposerStore.getState().onStreamDone(sid);
        // Reload messages from DB to get the final response
        socket.emit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: sid });
        setTabMessages(sid, (prev) => {
          const filtered = prev.filter((m) => !(m.role === 'system' && m.text.startsWith('⏳')));
          return [...filtered, { role: 'system', text: 'Agent finished while UI was disconnected. Messages reloaded.' }]; // EVENT-DRIVEN: triggered by WsEvent.QUERY_HEALTH_STATUS
        });
      }
    });

    // SESSION_IDLE (#2174 defect 2): the server freed a session's busy-slot WITHOUT a terminal frame — a bare
    // intake claim released/expired (a ghost: a recording's transcribe-only claim, an early-returning handler) or
    // a silent zombie evicted. A page that loaded/reconnected inside that window armed its composer busy from a
    // server snapshot (SESSIONS_RECONCILE.activeQuerySessionIds / MESSAGES_LOADED.isSessionBusy /
    // QUERY_IN_PROGRESS -> markServerBusy) and NO turn exists to send the terminal that would clear it: the server
    // healed it in silence and the page stayed busy ("still busy 100 s later"). Reconcile from the server's view,
    // by the named store action — only the SERVER-armed synthetic entry is released, never a local send (a local
    // turn ends with its own terminal frame). The "⏳ Claude is still working" pills QUERY_IN_PROGRESS /
    // CHECK_QUERY_HEALTH left behind are stale claims too; drop them (only when one exists — no needless render).
    socket.on(WsEvent.SESSION_IDLE, (data: SessionIdlePayload) => {
      const sid = data?.sessionId;
      if (!sid) return;
      useComposerStore.getState().markServerIdle(sid);
      const cached = tabCache.current.get(sid);
      if (cached?.messages.some((m) => m.role === 'system' && m.text.startsWith('⏳'))) {
        setTabMessages(sid, (prev) => prev.filter((m) => !(m.role === 'system' && m.text.startsWith('⏳'))));
      }
    });

    // SESSIONS_RECONCILE (issue #1161): authoritative server view of which sessions
    // still have a running query. Emitted on every connect — including Socket.IO
    // Connection State Recovery, where the `connect` event does NOT fire and the
    // CHECK_QUERY_HEALTH loop above never runs. For any session that is busy (per
    // composerStore) but NOT in activeQuerySessionIds: clear busy + reload messages.
    socket.on(WsEvent.SESSIONS_RECONCILE, (data: SessionsReconcilePayload) => {
      // Rail zero-session hang: this frame is emitted only AFTER the gateway authenticated
      // the socket, so a LIST_SESSIONS requested NOW cannot be dropped as unauthenticated
      // (the boot-time one can). Unsynced → ask again; the reply reconciles the store
      // (synced=true), including for an identity with ZERO sessions.
      if (needsSessionListSync(useSessionListStore.getState())) socket.emit(WsEvent.LIST_SESSIONS);
      const active = new Set(data?.activeQuerySessionIds || []);
      const csState = useComposerStore.getState();
      for (const [sid, tab] of tabCache.current) {
        if (getIsBusy(csState, sid) && !active.has(sid)) {
          csState.onStreamDone(sid);
          socket.emit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: sid });
          setTabMessages(sid, (prev) => {
            const filtered = prev.filter((m) => !(m.role === 'system' && m.text.startsWith('⏳')));
            return [...filtered, { role: 'system', text: 'Agent finished while UI was disconnected. Messages reloaded.' }]; // EVENT-DRIVEN: triggered by WsEvent.SESSIONS_RECONCILE
          });
        }
      }
      // ARM (Phase 2). activeQuerySessionIds used to be a one-directional signal: the loop above
      // could only CLEAR busy, never set it — so a session the server reports as RUNNING could
      // never be armed from it. Iterate `active` (the server's list), NOT tabCache: a session not
      // yet in the tab cache is invisible to that loop. This is also the ONLY busy signal on the
      // Socket.IO state-recovery path, which returns before QUERY_IN_PROGRESS is ever emitted.
      for (const sid of active) {
        if (!getIsBusy(useComposerStore.getState(), sid)) {
          useComposerStore.getState().markServerBusy(sid);
        }
      }
      // Phase 2 (composer-state-machine): reconcile outbound queues for all tracked sessions.
      const receivedClientMsgIds = data?.lastReceivedClientMsgIds || [];
      if (receivedClientMsgIds.length > 0) {
        for (const [sid] of tabCache.current) {
          useComposerStore.getState().onReconcile(sid, receivedClientMsgIds);
        }
      }
      // Phase 2 (session-load-fsm-migration): reconcile sessions not yet in saved-tabs.
      // Loads URL deep-link sessions + cross-device sessions that were not in the startup loop.
      if (data?.openSessionIds?.length) {
        slsApplyReconcile(data.openSessionIds);
      }
      // Prune stale idle sessions — remove any sessionId from idleSet that the server
      // no longer reports as a valid open session.
      useAgentListStore.getState().pruneStale([...tabCache.current.keys()]);
    });

    // Phase 2 (composer-state-machine): MESSAGE_RECEIVED — server persisted the user message.
    // Transitions composerStore: submitting → awaiting_stream. Clears the 5 s no-ack timer.
    socket.on(WsEvent.MESSAGE_RECEIVED, (data: MessageReceivedPayload) => {
      const { clientMsgId, serverMsgId, sessionId } = data ?? {};
      if (!clientMsgId || !sessionId) return;
      // Clear no-ack timer — message was received before timeout
      const timer = noAckTimers.current.get(clientMsgId);
      if (timer !== undefined) {
        clearTimeout(timer);
        noAckTimers.current.delete(clientMsgId);
      }
      useComposerStore.getState().onMessageReceived(sessionId, clientMsgId, serverMsgId);
    });

    // Phase 1 prompt queue (session-inflight-state-integrity): server accepted a mid-turn
    // prompt into its DB-backed queue instead of refusing it with "Still processing previous
    // request". Clear the 5 s no-ack timer FIRST (a queued prompt may legitimately wait minutes
    // for the turn ahead — without this the timer fires markSendFailed before PROMPT_QUEUED's
    // own store transition lands) then transition the outbound entry to `queued`.
    socket.on(WsEvent.PROMPT_QUEUED, (data: PromptQueuedPayload) => {
      const { sessionId, clientMsgId } = data ?? {};
      if (!sessionId || !clientMsgId) return;
      const timer = noAckTimers.current.get(clientMsgId);
      if (timer !== undefined) {
        clearTimeout(timer);
        noAckTimers.current.delete(clientMsgId);
      }
      useComposerStore.getState().onPromptQueued(sessionId, clientMsgId);
      setTabMessages(sessionId, (prev) => [
        ...prev,
        { role: 'system', text: 'Queued — will start when the current turn finishes.' },
      ]);
    });

    socket.on(WsEvent.MESSAGES_LOADED, (data: {
      messages: Array<{ id?: string; role: string; text: string; createdAt: string; model?: string; costUsd?: number; imagePaths?: string[] }>;
      sessionId: string;
      hasActiveProviderSession?: boolean;
      hasMore?: boolean;
      totalCount?: number;
      numTurns?: number;
      totalCostUsd?: number;
      lastInputTokens?: number;
      lastContextWindow?: number;
      lastContextTokens?: number;
      lastContextTokensSource?: import('@voice-assistant/shared').ContextTokensSource;
      /** Ledger-resolved occupancy union from the persisted pair (context-fullness
       *  Phase 0b) — {kind:'pending'} when the session never measured. */
      contextFullness?: ContextFullness;
      partialText?: string;
      partialReason?: import('@voice-assistant/shared').PartialReason;
      isSessionBusy?: boolean;
      /** The live turn's tool row (Phase 2) — server-known, so the reload never GUESSES which row
       *  is in flight. Declared here so a rename on either side is caught by tsc rather than
       *  silently resolving to undefined (→ no seed, no suppression, double ToolTrace). */
      inflightToolRowId?: string;
      partialTextLastEventId?: string;
      streamTailId?: string;
      activeProjects?: string[];
      pinnedProject?: string | null;
      activePlan?: { title: string; project: string; command: string } | null;
    }) => {
      const sid = data.sessionId;
      // Fast-forward lastEventId to the current Redis stream tail so REPLAY_EVENTS
      // doesn't re-emit CLAUDE_CHUNK events already captured in the DB messages.
      // Guard: only advance when the session is idle — if a stream is in progress,
      // the client may have missed events between its last-seen ID and the current
      // tail; fast-forwarding would make those events permanently unrestorable.
      if (data.streamTailId && data.streamTailId > lastEventIdRef.current && !data.isSessionBusy) {
        lastEventIdRef.current = data.streamTailId;
        try { localStorage.setItem('voice-assistant-last-event-id', data.streamTailId); } catch {}
      }
      // When session IS busy and we have partialText, advance to partialTextLastEventId
      // (the Redis ID of the last chunk already baked into partialText). This prevents
      // REPLAY_EVENTS from re-delivering chunks whose content is already in the seeded
      // bufferedText — which was the cause of duplicate text on refresh mid-stream.
      if (data.isSessionBusy && data.partialTextLastEventId && data.partialTextLastEventId > lastEventIdRef.current) {
        lastEventIdRef.current = data.partialTextLastEventId;
        try { localStorage.setItem('voice-assistant-last-event-id', data.partialTextLastEventId); } catch {}
      }
      // For relay sessions: losing the tmux process (hasActiveProviderSession=false) does NOT
      // mean conversation context is lost — the CLI can be re-spawned on next send.
      // Show a relay-specific notice instead of the generic "context expired" banner.
      // For non-relay sessions: missing claudeSessionId genuinely means the SDK context is gone.
      const isRelaySession = useSessionListStore.getState().sessions.get(sid)?.isRelay ?? false;
      setTabHasMore(sid, !!data.hasMore);
      if (data.messages && data.messages.length > 0) {
        const msgs: ChatMessage[] = data.messages.map((m) => {
          const decoded = decodeLoadedMessage(m);
          return {
            id: m.id,
            role: decoded.role,
            text: decoded.text,
            questions: decoded.questions,
            timestamp: m.createdAt,
            model: m.model,
            costUsd: m.costUsd,
            // Item E: re-render persisted attachments via the owner-scoped serve route.
            // A purged/expired/missing file 404s → ChatArea's onError hides the thumb →
            // falls back to the "[N image(s)]" text already in msg.text.
            images: (m.imagePaths && m.imagePaths.length > 0)
              ? m.imagePaths.map((p: string) => `/api/files/chat-image?path=${encodeURIComponent(p)}`)
              : undefined,
            
          };
        });
        if (!data.hasActiveProviderSession) {
          msgs.push({
            role: 'system',
            text: 'Previous session context expired — the agent will start fresh from this point.',
          });
        }
        // In-flight reconstruction (Phase 2). The decision lives in the pure
        // reconstructInflightOnLoad so an eval can drive the REAL logic rather than a copy.
        //
        // The old gate here was `if (data.partialText)`, with isSessionBusy consulted only INSIDE
        // it. A tool-heavy turn has NO partialText (partialDirty is set only in onChunk), so a
        // LIVE 90-minute turn reconstructed as IDLE and rendered as stalled. Busy is now armed
        // from the server's isSessionBusy alone.
        {
          const inflight = reconstructInflightOnLoad(data, data.messages);
          if (inflight.armBusy) {
            useComposerStore.getState().markServerBusy(sid);
          }
          // Equivalent to `if (inflight.seedText)` — seedText is set iff partialText is non-empty.
          // Gated on data.partialText directly so TS narrows it, and so the reconnect-path setpoint
          // (SE-CSM-STREAMBUF-DELETE) still reads the literal call it pins.
          if (data.partialText) {
            useComposerStore.getState().seedStreamingBuffer(sid, data.partialText);
          }
          if (inflight.seedToolActivities?.length) {
            // Seed the live trace from the persisted row so a reload shows ONE growing trace,
            // not a frozen DB row beside a fresh live one. Stamped with the current turn counter
            // so ChatArea's start/end turnIndex matching still pairs them.
            const turnIndex = getTurnCounter(sid);
            const seeded = inflight.seedToolActivities.map(
              (a) => ({ ...a, turnIndex }) as ToolActivityMessage & { turnIndex: number },
            );
            setTabToolActivities(sid, () => seeded);
            setTabInflightToolRowId(sid, inflight.inflightToolRowId);
          } else {
            // NO live turn to seed from => there is no row to suppress. Clearing here (rather than
            // only arming) is what stops a STALE id — armed by an earlier reload — from outliving
            // its turn and hiding that turn's now-completed tool row from the transcript forever.
            setTabInflightToolRowId(sid, undefined);
          }
          // Pushed LAST — the store's mergeSnapshotWithLiveTail keeps a trailing streaming
          // placeholder in final position, so a racing live chunk grows it instead of appending a
          // second bubble (the single-streaming-bubble invariant).
          if (inflight.streamingBubble) msgs.push(inflight.streamingBubble as any);
          else if (inflight.systemBubble) msgs.push(inflight.systemBubble as any);
        }
        // Store-first reconciliation (session-load FSM, #1237): onMessagesLoaded
        // merges the DB snapshot with any racing live-append tail via
        // mergeSnapshotWithLiveTail — a SYSTEM_NOTE / system pill / fresh user
        // send that landed after the fetch's DB query is preserved instead of
        // blind-replaced away (the DIFFICULTIES-LOG 2026-05-19 missing-pill race).
        // The legacy tabCache then MIRRORS the store's merged result so the two
        // lists cannot diverge (the old order — tabCache replace first, store
        // second — also dropped the preserved ⏳ busyMsg from the store; the
        // busyMsg special-case is subsumed by the general merge: an unmatched
        // trailing system message is live tail and survives).
        slsOnMessagesLoaded(sid, msgs, !!data.hasMore);
        {
          const merged = slsGetMessages(useSessionLoadStore.getState(), sid);
          setTabMessages(sid, () => merged);
        }
      } else {
        // Empty session — still mark as loaded so mode doesn't stay fetching_initial.
        // onMessagesLoaded's merge preserves locally-appended messages (e.g. the
        // first user send racing the initial empty-session fetch) instead of
        // wiping the store list to []. Mirror into the legacy cache.
        slsOnMessagesLoaded(sid, [], !!data.hasMore);
        {
          const merged = slsGetMessages(useSessionLoadStore.getState(), sid);
          setTabMessages(sid, () => merged);
        }
        // Persona-led first-session greeting (coach-tooltip-system.md §5.1 rule 2):
        // the empty-state → first-session transition is exactly "a brand-new session
        // whose MESSAGES_LOADED came back with zero rows" — this branch. In-thread
        // content (a plain assistant-role message), NOT a CoachLayer popover, so it
        // doesn't go through offer/evaluate/the active slot; markDiscovered records
        // it seen (reused as the "already shown" ledger) so a refresh before typing
        // never re-posts it (ephemeral like other client-synthesized system messages
        // in this file — accepted, not a regression: the user just lands in a normal
        // empty thread, and the replay "?" recovers the intro on request).
        if (pendingPersonaGreetingRef.current) {
          pendingPersonaGreetingRef.current = false;
          const sessionName = useSessionListStore.getState().sessions.get(sid)?.name ?? null;
          setTabMessages(sid, (prev) => [
            ...prev,
            { role: 'assistant', text: personaGreetingText(sessionName), timestamp: new Date().toISOString() },
          ]);
          coachMarkDiscovered(PERSONA_GREETING_ID);
        }
      }
      if (sid) {
        // Only activate this tab if no tab is active (initial load) or this IS the active tab.
        // Do NOT hijack focus when the user has already switched to a different tab —
        // that causes content bleed and busy state contamination.
        const wasInactive = !activeTabIdRef.current || activeTabIdRef.current !== sid;
        const shouldActivate = !activeTabIdRef.current || activeTabIdRef.current === sid;
        if (shouldActivate) {
          activeTabIdRef.current = sid;
          saveActiveTab(sid);
          setAndPersistCurrentSessionId(sid);
          // If this tab was not previously active, setTabMessages above ran with isActiveTab=false
          // (e.g. after newSession() clears activeTabIdRef or on first load). The tab cache has the
          // correct messages but React state was never updated. Sync now so messages are visible.
          if (wasInactive) syncTabToState(sid);
          // BUG-1: when a new session is created via FAB/picker, goToChat() runs before
          // the sid is known and pushes the placeholder `#chat`. Now that we know the
          // sid, replace the placeholder so the URL becomes `#s/<sid>` and per-session
          // history navigation works as designed.
          if (typeof window !== 'undefined' && window.location.hash === '#chat') {
            const stateObj = { vaView: 'chat', sessionId: sid };
            window.history.replaceState(stateObj, '', buildSessionHash(sid));
          }
        }
        // Auto-send pending prompt for new sessions (e.g. plan review)
        if (pendingNewSession.current && pendingPromptRef.current && shouldActivate) {
          pendingNewSession.current = false;
          const prompt = pendingPromptRef.current;
          pendingPromptRef.current = null;
          // Defer send slightly so the tab is fully initialized
          setTimeout(() => {
            const s = socketRef.current;
            if (!s) return;
            const clientMsgId = crypto.randomUUID();
            useComposerStore.getState().commitSend(sid, { text: prompt, attachments: [], clientMsgId });
            incTurnCounter(sid);
            setTabToolActivities(sid, () => []);
            setTabMessages(sid, (prev) => [...prev, { role: 'user', text: prompt, timestamp: new Date().toISOString() }]);
            s.emit(WsEvent.SEND_TEXT, { text: prompt, sessionId: sid, clientMsgId });
          }, 100);
        }
        // Always add to open tabs (idempotent)
        setOpenTabs((prev) => {
          const tabs = prev.includes(sid) ? prev : [sid, ...prev].slice(0, MAX_TABS);
          saveOpenTabs(tabs);
          return tabs;
        });
        // Initialize tab cache
        getTab(sid);
      }
      if (data.numTurns != null || data.totalCostUsd != null) {
        setTabSessionMeta(sid, {
          numTurns: data.numTurns,
          totalCostUsd: data.totalCostUsd,
        });
      }
      if (data.lastInputTokens || data.lastContextWindow) {
        setTabSessionHealth(sid, {
          inputTokens: data.lastInputTokens,
          contextWindow: data.lastContextWindow,
        });
      }
      // Occupancy union from the persisted pair (context-fullness Phase 0b) —
      // {kind:'pending'} when never measured; reconcileFromLoad ignores pending.
      if (data.contextFullness) reconcileFromLoad(sid, data.contextFullness);
      // Restore active project chips and plan chip on reconnect
      if (data.activeProjects || data.pinnedProject !== undefined || data.activePlan !== undefined) {
        const patch: Partial<SessionEntry> = {};
        if (data.activeProjects != null) patch.activeProjects = data.activeProjects;
        if (data.pinnedProject !== undefined) patch.pinnedProject = data.pinnedProject;
        if (data.activePlan !== undefined) patch.activePlan = data.activePlan;
        if (Object.keys(patch).length > 0) useSessionListStore.getState().patch(data.sessionId, patch);
      }
      // Always follow up with a full health fetch so modelsUsed, toolCallCount,
      // createdAt, fileSize, etc. are populated — and so sessions with no token
      // data in the DB (fresh or legacy rows) still get their ctx pill updated.
      // By this point the backend has finished processing SWITCH_SESSION, so
      // SESSION_HEALTH will correctly reflect the just-loaded session.
      socket.emit(WsEvent.SESSION_HEALTH);
    });

    socket.on(WsEvent.OLDER_MESSAGES_LOADED, (data: {
      messages: Array<{ id?: string; role: string; text: string; createdAt: string; model?: string; costUsd?: number; imagePaths?: string[] }>;
      sessionId: string;
      hasMore?: boolean;
    }) => {
      const sid = data.sessionId;
      setTabHasMore(sid, !!data.hasMore);
      if (data.messages && data.messages.length > 0) {
        const olderMsgs: ChatMessage[] = data.messages.map((m) => {
          const decoded = decodeLoadedMessage(m);
          return {
            id: m.id,
            role: decoded.role,
            text: decoded.text,
            questions: decoded.questions,
            timestamp: m.createdAt,
            model: m.model,
            costUsd: m.costUsd,
            // Item E: re-render persisted attachments via the owner-scoped serve route.
            // A purged/expired/missing file 404s → ChatArea's onError hides the thumb →
            // falls back to the "[N image(s)]" text already in msg.text.
            images: (m.imagePaths && m.imagePaths.length > 0)
              ? m.imagePaths.map((p: string) => `/api/files/chat-image?path=${encodeURIComponent(p)}`)
              : undefined,
            
          };
        });
        setTabMessages(sid, (prev) => [...olderMsgs, ...prev]);
        // Phase 1 dual-write: prepend older messages in sessionLoadStore.
        slsOnOlderLoaded(sid, olderMsgs, !!data.hasMore);
      }
    });

    socket.on(WsEvent.TRANSCRIPTION, (data: { text: string; sessionId?: string; correction?: ChatMessage['correction'] }) => {
      // Transcribe-only mode: deliver text to input bar, don't add to chat
      if (transcribeOnlyCallbackRef.current) {
        if (transcribeTimeoutRef.current) {
          clearTimeout(transcribeTimeoutRef.current);
          transcribeTimeoutRef.current = null;
        }
        const cb = transcribeOnlyCallbackRef.current;
        transcribeOnlyCallbackRef.current = null;
        // Persist the transcript to the *originating* session's draft based on
        // data.sessionId rather than the currently-active tab. If the user
        // PTT'd in session A and switched to list view (or to session B)
        // before the response landed, the legacy code-path saved the draft
        // against the wrong session via activeTabIdRef — or no session if the
        // InputBar had unmounted, dropping the transcript on the floor.
        if (data.text && data.sessionId) {
          // composerStore is the single source of truth; App.tsx subscribes and re-renders.
          useComposerStore.getState().setDraft(data.sessionId, data.text);
        }
        cb(data.text || '');
        return;
      }
      if (getSshMode(useSshTerminalStore.getState(), SSH_PRIMARY_TERMINAL_ID) === 'connected') {
        if (data.text?.trim()) {
          const trimmed = data.text.trim();
          // store (authoritative): append a voice command, mirroring the store's
          // own last voice-history entry (appendVoiceHistory itself does not dedup).
          const storeHist = useSshTerminalStore.getState().terminals.get(SSH_PRIMARY_TERMINAL_ID)?.voiceHistory ?? [];
          if (storeHist[storeHist.length - 1] !== trimmed) {
            sshStoreAppendVoiceHistory(SSH_PRIMARY_TERMINAL_ID, trimmed);
          }
        }
        return;
      }
      const sid = data.sessionId || activeTabIdRef.current;
      const images = pendingImagesRef.current.length > 0
        ? [...pendingImagesRef.current]
        : undefined;
      pendingImagesRef.current = [];
      setTabMessages(sid, (prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'user' && last.text === data.text && !images) {
          return prev;
        }
        return [...prev, { role: 'user', text: data.text, images, timestamp: new Date().toISOString(), correction: data.correction }];
      });
      // Atomic send commit: push submitting entry + clear draft in one store write.
      // Uses the clientMsgId from sendAudio; falls back to setDraft clear if absent.
      const voicePending = pendingVoiceClientMsgId.current;
      if (voicePending && voicePending.sessionId === sid && data.text) {
        pendingVoiceClientMsgId.current = null;
        useComposerStore.getState().commitSend(sid, {
          text: data.text,
          attachments: images ? [] : [],
          clientMsgId: voicePending.clientMsgId,
        });
      } else if (sid) {
        useComposerStore.getState().setDraft(sid, '');
      }
    });

    socket.on(WsEvent.CLAUDE_CHUNK, (data: { text?: string; content?: string; seq?: number; isDone?: boolean; sessionId?: string; model?: string; _lastEventId?: string }) => {
      trackEventId(data as any);
      const sid = data.sessionId || activeTabIdRef.current;
      // Transition composerStore: awaiting_stream → streaming (first chunk), or increment chunkCount.
      // Thread the chunk text so the machine accumulates `bufferedText` — the canonical
      // streaming-text source the flush scheduler reads (streambuf-to-outbound migration, Phase 2).
      if (sid) {
        // Non-local producer catch-all (#1247): a session streaming text with no local
        // commitSend (autonomous wave, remote Continue / remote slash) has no outbound
        // entry, so onStreamChunk would no-op and the composer / orange-dot read idle.
        // markServerBusy is idempotent and allocation-free when already busy, so calling
        // it per chunk is cheap and a no-op for local sends.
        const store = useComposerStore.getState();
        store.markServerBusy(sid);
        store.onStreamChunk(sid, data as any);

        // Mid-stream gap recovery: this chunk arrived ahead of the expected seq → the
        // composerStore has buffered it; ask the backend to re-send the missing chunks.
        // The backend re-reads the user's Redis stream from the last in-order event id
        // (recovery.lastEventId) and re-emits CLAUDE_CHUNK events with seq >= expectedSeq.
        // Lost-in-transit chunks are recoverable because the gateway appends to Redis
        // BEFORE emitting. WsEvent (not a bare string) so tsc enforces the wire name (#1532 class).
        const recovery = getStreamGapRecovery(useComposerStore.getState(), sid);
        if (recovery && data.seq !== undefined && data.seq > recovery.expectedSeq) {
          socket.emit(WsEvent.REQUEST_MISSING_CHUNKS, {
            sessionId: sid,
            expectedSeq: recovery.expectedSeq,
            lastEventId: recovery.lastEventId,
          } satisfies RequestMissingChunksPayload);
        }
      }

      // Only trigger TTS callbacks for active tab (TTS has its own buffer, fine per-chunk)
      if (isActiveTab(sid!)) chunkCallbackRef.current?.(data.content || data.text || '');

      // Batch React state updates — coalesce to one render per animation frame
      if (sid) scheduleChunkFlush(sid, data.model);
    });

    socket.on(WsEvent.CLAUDE_DONE, (data: { fullText: string; sessionId?: string; numTurns?: number; totalCostUsd?: number; messageCount?: number; resultSubtype?: string; _lastEventId?: string; model?: string; isConsultation?: boolean; inputTokens?: number; outputTokens?: number; costUsd?: number; isSimulated?: boolean; sessionNumTurns?: number; sessionTotalCostUsd?: number }) => {
      trackEventId(data);
      const sid = data.sessionId || activeTabIdRef.current;
      // Flush any batched chunks immediately before finalizing
      if (sid) flushChunksNow(sid);
      // Auto-refresh file drawer if open — agent may have created/modified files.
      // Open-state is owned by drawerStore (panel==='files'); refresh still lives on
      // fileDrawerStore (the refreshToken mechanism).
      if (getDrawerPanel(useDrawerStore.getState()) === 'files') useFileDrawerStore.getState().refresh();      // Transition composerStore: streaming → done
      if (sid) useComposerStore.getState().onStreamDone(sid);
      // The turn is over: its tool row is now ordinary history and must render as such. Disarm the
      // in-flight id in the SAME breath as busy goes false — a turn that ends WITHOUT a reload never
      // re-runs MESSAGES_LOADED, so this is the only place the arm from an earlier mid-turn reload
      // would otherwise be released. (shouldSuppressToolRow's busy gate already makes a missed clear
      // non-fatal; this keeps a stale id from suppressing the PREVIOUS turn's row during the NEXT one.)
      if (sid) setTabInflightToolRowId(sid, undefined);
      // Close-out any unresolved "⏳ Claude is still working" / "⏳ Agent working"
      // pills inserted by QUERY_IN_PROGRESS or QUERY_HEALTH_STATUS earlier — without
      // this, the in-progress indicator stays in chat history forever and users read
      // it as "agent is still working RIGHT NOW," even though busy has cleared. The
      // result is a confusing UX where the cancel button is gone (correct — not busy)
      // and the send button is enabled (correct — not busy) but the stale pill says
      // otherwise. Append a "✓ Streaming resumed" pill iff there's an unclosed
      // ⏳ pill in the message list (count ⏳ minus count ✓ resumed > 0).
      if (sid) {
        setTabMessages(sid, (prev) => {
          let unclosed = 0;
          for (const m of prev) {
            if (m.role !== 'system' || !m.text) continue;
            if (m.text.startsWith('⏳ Claude is still working') || m.text.startsWith('⏳ Agent working')) {
              unclosed++;
            } else if (m.text.startsWith('✓ Streaming resumed')) {
              unclosed--;
            }
          }
          if (unclosed > 0) {
            return [...prev, { role: 'system' as const, text: '✓ Streaming resumed — agent finished.' }];
          }
          return prev;
        });
      }

      // Check if this CLAUDE_DONE finalizes an Opus consultation stream
      const tabState = tabCache.current.get(sid!);
      const isOpusConsultation = data.isConsultation || tabState?.consultation?.provider === 'opus';
      if (isOpusConsultation) setTabConsultation(sid, null);

      if (isActiveTab(sid!)) doneCallbackRef.current?.();
      const displayTurns = data.messageCount ?? data.sessionNumTurns ?? data.numTurns;
      if (displayTurns != null || data.sessionTotalCostUsd != null || data.totalCostUsd != null) {
        setTabSessionMeta(sid, { numTurns: displayTurns, totalCostUsd: data.sessionTotalCostUsd ?? data.totalCostUsd });
      }
      fetchUsage();
      const doneTimestamp = new Date().toISOString();
      setTabMessages(sid, (prev) => {
        const last = prev[prev.length - 1];
        let msgs: ChatMessage[];
        const consultationMeta = isOpusConsultation ? {
          provider: 'opus' as const,
          ...(data.costUsd != null ? { costUsd: data.costUsd } : {}),
          ...(data.inputTokens != null ? { inputTokens: data.inputTokens } : {}),
          ...(data.outputTokens != null ? { outputTokens: data.outputTokens } : {}),
        } : {};
        if (last?.streaming) {
          msgs = [...prev.slice(0, -1), {
            role: (isOpusConsultation ? 'consultation' : 'assistant') as ChatMessage['role'],
            text: data.fullText || last.text,
            timestamp: doneTimestamp,
            model: data.model,
            ...consultationMeta,
          }];
        } else if (data.fullText) {
          // No streaming placeholder exists (SDK delivered reply atomically
          // without text_delta chunks). Append the full text as a fresh
          // assistant message so it isn't silently dropped.
          msgs = [...prev, {
            role: (isOpusConsultation ? 'consultation' : 'assistant') as ChatMessage['role'],
            text: data.fullText,
            timestamp: doneTimestamp,
            model: data.model,
            ...consultationMeta,
          }];
        } else {
          msgs = prev;
        }
        if (data.resultSubtype && data.resultSubtype !== 'success') {
          const label = data.resultSubtype === 'error_max_turns' ? 'Agent reached the turn limit'
            : data.resultSubtype === 'error_max_budget_usd' ? 'Agent reached the budget limit'
            : data.resultSubtype === 'error_during_execution' ? 'Agent encountered an execution error'
            : `Agent stopped (${data.resultSubtype})`;
          return [...msgs, { role: 'system' as const, text: `⚠ ${label}. Response may be incomplete.` }];
        }
        return msgs;
      });
    });

    socket.on(WsEvent.SUMMARY_READY, (data: { summary: string }) => {
      setSummarizing(false);
      summaryCallbackRef.current?.(data.summary);
    });

    socket.on(WsEvent.ERROR, (data: { message: string; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (sid) flushChunksNow(sid);
      if (sid) useComposerStore.getState().onPipelineError(sid);      // If a transcribe-only request was in flight, clear the callback so the mic button
      // doesn't stay locked in "transcribing" (⏳) state forever after a server error
      // (e.g. "Audio too short" or "Still processing previous request").
      if (transcribeOnlyCallbackRef.current) {
        if (transcribeTimeoutRef.current) {
          clearTimeout(transcribeTimeoutRef.current);
          transcribeTimeoutRef.current = null;
        }
        const cb = transcribeOnlyCallbackRef.current;
        transcribeOnlyCallbackRef.current = null;
        cb('');
      }
      setTabMessages(sid, (prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'error' && last.text === data.message) return prev;
        return [...prev, { role: 'error', text: data.message }];
      });
    });

    // Session management events
    socket.on(WsEvent.SESSIONS_LIST, (data: { sessions: SessionSummary[] }) => {
      const userSessions = data.sessions || [];
      // Authoritative server snapshot — REPLACE the canonical set + prune.
      // SessionEntry === SessionSummary, so rollingSignpost / numTurns /
      // totalCostUsd / etc. are carried verbatim — no parallel sessionMeta
      // populate needed (used to be a 2nd legacy state write).
      useSessionListStore.getState().reconcile(userSessions);
      // Desktop-home rail loading state (issue #2201): the first SESSIONS_LIST reconcile
      // is the "sessions are now known" signal — until it lands the client genuinely does
      // not know whether the list is empty, so renderers show a designed loading state
      // instead of a false empty/error. Fires on every (re)connect; idempotent.

      // Reconcile open tabs — remove any that don't belong to this user's sessions or are cleared
      const validIds = new Set(userSessions.filter((s) => s.status !== 'cleared').map((s) => s.id));
      setOpenTabs((prev) => {
        const cleaned = prev.filter((id) => validIds.has(id));
        if (cleaned.length !== prev.length) {
          saveOpenTabs(cleaned);
          // If active tab was pruned, switch to first valid tab
          if (activeTabIdRef.current && !validIds.has(activeTabIdRef.current)) {
            const next = cleaned[0];
            activeTabIdRef.current = next;
            saveActiveTab(next);
            setAndPersistCurrentSessionId(next ?? null);
            if (next) syncTabToState(next);
          }
          // Clean orphan tab caches
          for (const id of prev) {
            if (!validIds.has(id)) tabCache.current.delete(id);
          }
        }
        return cleaned;
      });
    });

    socket.on(WsEvent.SESSION_SWITCHED, (data: { sessionId: string; hasActiveProviderSession?: boolean; hasMore?: boolean; messages: Array<{ id?: string; role: string; text: string; createdAt: string; model?: string; costUsd?: number; imagePaths?: string[] }> }) => {
      const sid = data.sessionId;
      activeTabIdRef.current = sid;
      saveActiveTab(sid);
      setAndPersistCurrentSessionId(sid);
      setTabHasMore(sid, !!data.hasMore);

      // Mirror the MESSAGES_LOADED builder (~:942): route every persisted row through
      // decodeLoadedMessage so a role='agent_question' turn re-parses its JSON `text`
      // back into `questions` → ChatArea renders the AgentQuestionCard instead of raw
      // JSON. SESSION_SWITCHED is the cold-deep-link / boot-restore load path, so without
      // this decode a persisted AskUserQuestion turn rendered as raw text on first open.
      const msgs: ChatMessage[] = data.messages.map((m) => {
        const decoded = decodeLoadedMessage(m);
        return {
          id: m.id,
          role: decoded.role,
          text: decoded.text,
          questions: decoded.questions,
          timestamp: m.createdAt,
          model: m.model,
          costUsd: m.costUsd,
          // Item E: re-render persisted attachments via the owner-scoped serve route.
          // A purged/expired/missing file 404s → ChatArea's onError hides the thumb →
          // falls back to the "[N image(s)]" text already in m.text.
          images: (m.imagePaths && m.imagePaths.length > 0)
            ? m.imagePaths.map((p: string) => `/api/files/chat-image?path=${encodeURIComponent(p)}`)
            : undefined,
        };
      });
      if (data.messages.length > 0 && !data.hasActiveProviderSession) {
        // For relay sessions losing the tmux process doesn't mean context is lost —
        // the CLI re-spawns on next send. Show an accurate status message instead.
        const isRelay = useSessionListStore.getState().sessions.get(sid)?.isRelay ?? false;
        msgs.push({
          role: 'system',
          text: isRelay
            ? 'Relay process not running — send a message to reconnect to the CLI.'
            : 'Previous session context expired — the agent will start fresh from this point.',
        });
      }

      // Phase 5: SESSION_SWITCHED is the success-reply to a SWITCH_SESSION emit.
      // Clears the fetch-timer armed by openSession(), reconciles the snapshot
      // against any racing live-append tail (mergeSnapshotWithLiveTail, #1237 —
      // SESSION_SWITCHED is the boot/reconnect path, exactly where a mid-turn
      // snapshot races a live SYSTEM_NOTE on mobile PWA resume), and transitions
      // mode to 'loaded'. Without the timer-clear, the fetch-timer fired 10 s
      // later and silently put the session in 'error' — invisible most of the
      // time but visibly broken on Retry.
      slsOnSessionSwitched(sid, msgs, !!data.hasMore);

      // Update tab cache (mirror of the store's merged result) and React state
      const tab = getTab(sid);
      tab.messages = slsGetMessages(useSessionLoadStore.getState(), sid);
      tab.toolActivities = [];
      tab.sessionMeta = {};
      tab.sessionHealth = null;

      // Add to open tabs if not already
      setOpenTabs((prev) => {
        const tabs = prev.includes(sid) ? prev : [sid, ...prev].slice(0, MAX_TABS);
        saveOpenTabs(tabs);
        return tabs;
      });

      syncTabToState(sid);
    });

    socket.on(WsEvent.SESSION_RENAMED, (data: { sessionId: string; name: string }) => {
      useSessionListStore.getState().rename(data.sessionId, data.name);
    });

    socket.on(WsEvent.CONTEXT_TAG_UPDATED, (data: { sessionId: string; tag: string }) => {
      useSessionListStore.getState().patch(data.sessionId, { contextTag: data.tag });
    });

    // Latest captured intent → top bar Row 2 (model-meta-hardening Phase 5).
    // Mirrors CONTEXT_TAG_UPDATED: patch the session-list store so the bar
    // reflects the most recent classified turn's work category + the model that
    // ran, live. Reload restores the same values server-authoritatively from the
    // SESSIONS_LIST snapshot (sessions.service.getUserSessions latestIntent join).
    socket.on(WsEvent.LATEST_INTENT_UPDATED, (data: { sessionId: string; intentType: string; model: string | null }) => {
      useSessionListStore.getState().patch(data.sessionId, {
        latestIntentType: data.intentType,
        latestIntentModel: data.model ?? null,
      });
    });

    socket.on(WsEvent.PROJECTS_DETECTED, (data: { sessionId: string; projects: string[]; primary: string | null; source: string; activePlan?: { title: string; project: string; command: string } | null }) => {
      useSessionListStore.getState().patch(data.sessionId, {
        activeProjects: data.projects,
        activePlan: data.activePlan ?? null,
      });
    });

    socket.on(WsEvent.SESSION_DELETED, (data: { sessionId: string }) => {
      useSessionListStore.getState().remove(data.sessionId);
      clearForSession(data.sessionId);
      // Remove from open tabs
      tabCache.current.delete(data.sessionId);
      setOpenTabs((prev) => {
        const tabs = prev.filter((id) => id !== data.sessionId);
        saveOpenTabs(tabs);
        return tabs;
      });
    });

    socket.on(WsEvent.SESSION_RESUME_FAILED, (data: { sessionId?: string }) => {
      const sid = data?.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        {
          role: 'system',
          text: 'Session context was lost — the agent is starting fresh. Messages above are history only.',
        },
      ]);
      if (isActiveTab(sid!)) resumeFailedRef.current?.();
    });

    socket.on(WsEvent.TOOL_ACTIVITY, (data: ToolActivityMessage & { sessionId?: string; _lastEventId?: string }) => {
      trackEventId(data);
      const sid = data.sessionId || activeTabIdRef.current;
      // Tool-only turns (the agent is calling tools, no text chunks yet) from a non-local
      // producer still mean the session is working — keep the composer / orange-dot busy
      // (#1247). Idempotent + allocation-free when already busy.
      if (sid) useComposerStore.getState().markServerBusy(sid);
      // Phase 0b: type:'context' events are mid-turn occupancy signals, NOT tool-trace
      // rows. The backend flushes them through the same narrated trace channel, so —
      // mirroring tool-call-log-flusher (which aggregates start/end only) — they are
      // routed to the contextFullnessStore here and NEVER appended to the trace, else
      // a 'context' pseudo-tool would render in the UI. (The shared
      // ToolActivityMessage['type'] union predates this event kind — compare via a
      // widened local; widening the shared union is flagged as a follow-up.)
      const activityType: string = data.type;
      if (activityType === 'context') {
        if (sid) observeContextActivity(sid, data);
        return;
      }
      setTabToolActivities(sid, (prev) => [...prev, { ...data, turnIndex: getTurnCounter(sid) }]);
    });

    socket.on(WsEvent.PROMPT_CANCELLED, (data: { sessionId?: string }) => {
      cancelPendingRef.current = false;
      const sid = data?.sessionId || activeTabIdRef.current;
      if (sid) useComposerStore.getState().onStreamDone(sid);      setTabMessages(sid, (prev) => {
        const last = prev[prev.length - 1];
        if (last?.streaming) {
          return [
            ...prev.slice(0, -1),
            { ...last, streaming: false },
            { role: 'system', text: 'Prompt cancelled.' },
          ];
        }
        return [...prev, { role: 'system', text: 'Prompt cancelled.' }];
      });
      if (isActiveTab(sid!)) doneCallbackRef.current?.();
    });

    socket.on(WsEvent.CONTEXT_LOADED, (data: { projects: string[] }) => {
      contextLoadedRef.current?.(data.projects);
    });

    socket.on(WsEvent.CONTEXT_COMPACTED, (data: { sessionId?: string }) => {
      const sid = data?.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        {
          role: 'system',
          text: '⚠ Context compacted — the agent summarized older messages to fit the context window. It may not recall earlier details precisely.',
        },
      ]);
    });

    socket.on(WsEvent.CONTEXT_HIGH, (data: { contextPct: number; threshold: number; maxChatTurns?: number; message: string; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      const isMaxTurns = data.contextPct === 0 && data.threshold === 0;
      const turns = data.maxChatTurns != null ? ` turns:${data.maxChatTurns}` : '';
      const text = isMaxTurns
        ? `⚠ Auto-reset · turn limit${turns}`
        : `⚠ Auto-reset · ctx ${data.contextPct}%≥${data.threshold}%${turns}`;
      setTabMessages(sid, (prev) => [...prev, { role: 'system', text }]);
      // Gate the input bar for the duration of /handoff orchestration. The
      // previous user-turn's CLAUDE_DONE cleared busy; without re-gating here
      // the input flips to "Type a message…" while /handoff is still running
      // in the background, letting the user send a message that the backend
      // then rejects with "Still processing previous request". Released on
      // CONTEXT_DOCS_UPDATED (orchestration-complete terminal). Issue #1141.
      if (sid) useComposerStore.getState().startOrchestration(sid);
    });

    socket.on(WsEvent.LOOP_SUSPECTED, (data: { reason: string; tool: string; message: string; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'system', text: `⚠ Loop detected · ${data.tool} (${data.reason}) — consider stopping or redirecting` },
      ]);
    });

    socket.on(WsEvent.CONTEXT_DOCS_UPDATED, (data: { contextPct: number; message: string; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'system', text: `✓ Knowledge saved. Fresh session ready.` },
      ]);
      // Orchestration-complete terminal — release the input-bar gate that
      // CONTEXT_HIGH / CONTEXT_RESETTING set when /handoff started. Issue #1141.
      if (sid) useComposerStore.getState().endOrchestration(sid);
    });

    socket.on(WsEvent.AUTO_CONTINUATION, (data: { sessionId?: string; reason: string; message: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'system', text: `↻ ${data.message}` },
      ]);
      // Re-assert composer busy for the backend-originated continuation turn.
      // The continuation is a non-local producer (no commitSend), so without this
      // the composer shows idle while the agent is still working, and the
      // SESSIONS_RECONCILE getIsBusy guard incorrectly skips the message reload
      // on reconnect. Released by the isolated CLAUDE_DONE listener below (#1247).
      if (sid) {
        autoContinuingRef.current.add(sid);
        useComposerStore.getState().startOrchestration(sid);
      }
    });

    // Clear the auto-continuation busy gate when the continued turn ends (#1247).
    // Separate, additional CLAUDE_DONE listener — only touches the auto-continuation
    // gate; does NOT interfere with the main CLAUDE_DONE handler elsewhere.
    socket.on(WsEvent.CLAUDE_DONE, (data: { sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (sid && autoContinuingRef.current.has(sid)) {
        autoContinuingRef.current.delete(sid);
        useComposerStore.getState().endOrchestration(sid);
      }
    });

    socket.on(WsEvent.AGENT_QUESTION, (data: { questions: AskUserQuestionData[]; sessionId?: string; serverMsgId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (sid && data.serverMsgId) {
        useComposerStore.getState().onAgentQuestion(sid, data.serverMsgId, data.questions);
      }
      setTabMessages(sid, (prev) => [
        ...prev,
        {
          role: 'agent_question' as const,
          text: '',
          questions: data.questions,
        },
      ]);
    });

    socket.on(WsEvent.TOOL_APPROVAL_REQUEST, (data: { approvalToken: string; toolName: string; toolArgs: any; reason?: string; serverMsgId?: string }) => {
      // dynamically import useToolApprovalStore to avoid circular deps if they exist
      import('../state/toolApprovalStore').then(({ useToolApprovalStore }) => {
        useToolApprovalStore.getState().setRequest(data);
      });
    });

    // Provider failover events
    socket.on(WsEvent.PROVIDER_SWITCHED, (data: { from: string; to: string; reason: string; error?: string; sessionId?: string; temporary?: boolean }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      const providerLabel = data.to === 'google' ? 'Gemini' : 'Claude';
      const reasonLabel = data.reason === 'rate_limit' ? 'rate limited'
        : data.reason === 'overloaded' ? 'overloaded'
        : data.reason === 'billing' ? 'subscription limit reached'
        : 'unavailable';
      if (data.temporary) {
        // One-message-only switch — inline pill, don't claim session changed identity
        setTabMessages(sid, (prev) => [
          ...prev,
          {
            role: 'system',
            text: `⚡ This response from ${providerLabel} (original provider ${data.from === 'google' ? 'Gemini' : 'Claude'} ${reasonLabel})`,
          },
        ]);
      } else {
        setTabMessages(sid, (prev) => [
          ...prev,
          {
            role: 'system',
            text: `⚡ Switched to ${providerLabel} — Claude is ${reasonLabel}. The agent continues with full capabilities.`,
          },
        ]);
        // Only update global provider info for non-temporary (permanent) switches
        setProviderInfo((prev) => prev ? { ...prev, provider: data.to as any, reason: data.reason } : null);
      }
    });

    socket.on(WsEvent.PROVIDER_ERROR, (data: { sessionId?: string; provider: string; code: number; message: string; retried: number; offerFallback: boolean; authRequired?: boolean; remedy?: 'claude_login' }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (!sid) return;
      setProviderErrors((prev) => {
        const next = new Map(prev);
        next.set(sid, { provider: data.provider, message: data.message, offerFallback: data.offerFallback, authRequired: data.authRequired, remedy: data.remedy });
        return next;
      });
    });

    socket.on(WsEvent.PROVIDER_STATUS, (data: ActiveProviderInfo) => {
      setProviderInfo(data);
    });

    // SDK telemetry events
    socket.on(WsEvent.RATE_LIMIT_WARNING, (data: { status: string; utilization?: number; resetsAt?: number; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (data.status === 'rejected') {
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: '⚠ API rate limit hit. The request was rejected. Please wait a moment before trying again.' },
        ]);
      } else if (data.status === 'allowed_warning') {
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: `⚠ Approaching API rate limit (${data.utilization ? Math.round(data.utilization * 100) + '% utilized' : 'high usage'}). Requests may slow down.` },
        ]);
      }
    });

    // Classifier fell back to regex — Haiku unavailable, Sonnet continues normally.
    // Shown once per prompt as a muted inline notice (not an error).
    socket.on(WsEvent.CASCADE_DEGRADED, (data: { reason: string; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => {
        // Deduplicate: skip if the last system message already said this
        const last = prev[prev.length - 1];
        if (last?.role === 'system' && last.text?.includes('smart routing offline')) return prev;
        return [
          ...prev,
          { role: 'system', text: '⚙ Smart routing offline — Sonnet is handling your request directly. Opus escalation suggestions may be missed.' },
        ];
      });
    });

    // Classifier recovered from degraded state — show inline notice
    socket.on(WsEvent.CASCADE_RECOVERED, () => {
      const sid = activeTabIdRef.current;
      setTabMessages(sid, (prev) => {
        // Only show recovery if we previously showed a degraded message
        const hasDegraded = prev.some(m => m.role === 'system' && m.text?.includes('smart routing offline'));
        if (!hasDegraded) return prev;
        // Deduplicate
        const last = prev[prev.length - 1];
        if (last?.role === 'system' && last.text?.includes('smart routing restored')) return prev;
        return [
          ...prev,
          { role: 'system', text: '⚙ Smart routing restored — Haiku classifier is back online.' },
        ];
      });
    });

    socket.on(WsEvent.TOOL_PROGRESS, (data: { tool: string; elapsedSeconds?: number; sessionId?: string; _lastEventId?: string }) => {
      trackEventId(data);
      const sid = data.sessionId || activeTabIdRef.current;
      // Long-running tool progress for a non-local producer — keep busy (#1247). Idempotent.
      if (sid) useComposerStore.getState().markServerBusy(sid);
      setTabToolActivities(sid, (prev) => [
        ...prev,
        { type: 'progress', tool: data.tool, elapsedSeconds: data.elapsedSeconds, turnIndex: getTurnCounter(sid) },
      ]);
    });

    socket.on(WsEvent.SDK_ERROR, (data: { subtype: string; errors?: string[]; sessionId?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      if (data.subtype?.startsWith('assistant_error_')) {
        const errType = data.subtype.replace('assistant_error_', '');
        const label = errType === 'rate_limit' ? 'Rate limited'
          : errType === 'billing_error' ? 'Billing error'
          : errType === 'server_error' ? 'Server error'
          : errType;
        const hint = errType === 'billing_error'
          ? 'Your API key credit balance is too low. Top up at console.anthropic.com, then **generate a new API key** and update it in Settings — Anthropic may cache the "blocked" state on the old key for up to 60 minutes.'
          : 'the agent is retrying automatically.';
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: `⚠ ${label} — ${hint}` },
        ]);
      } else if (data.subtype === 'thinking_block_corrupted') {
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: '⚠ Reasoning context refreshed — please resend your message.' },
        ]);
      } else if (data.subtype === 'askuserquestion_incomplete') {
        // The agent tried to ask a question but its tool input was truncated/incomplete
        // (abort / max_tokens / mid-block stop) so no question card could be built. #1645.
        setTabMessages(sid, (prev) => [
          ...prev,
          { role: 'system', text: '⚠ The agent tried to ask a question, but its input was incomplete — please resend your message.' },
        ]);
      }
    });

    // Phase 2 of plans/gemini-tool-bridge-hardening.md — graceful-recovery pills.
    // Backend persists the role='system' message in DB AND emits this event so the
    // live UI inserts an immediate pill via the existing humanizeSystemEvent path.
    // Reload-safe because the persisted row is part of the message list.
    socket.on(WsEvent.SYSTEM_NOTE, (data: { sessionId?: string; text: string; reason?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'system', text: data.text },
      ]);
    });

    // Classifier-suggested Opus consultation — prompt is held, enter selection mode
    socket.on(WsEvent.OPUS_SUGGESTED, (data: { sessionId: string; prompt: string; reason: string }) => {
      opusSuggestedRef.current?.(data);
    });

    // Listen for OPUS_CONSULT/DISMISSED acks (no-op, backend handles everything)

    // Cross-model consultation live indicator — scoped to the session that triggered it
    socket.on(WsEvent.CONSULTATION_START, (data: { sessionId?: string; provider: string; question?: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabConsultation(sid, { provider: data.provider, question: data.question });
    });

    // Prompt intercept events (smart routing)
    socket.on(WsEvent.SYSTEM_ACTION, (data: { sessionId?: string; text: string; intentType?: string; model?: string; type?: string; provider?: string; terminal?: boolean; inputTokens?: number; outputTokens?: number; costUsd?: number }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      // Clear the per-tab consultation indicator once the result arrives
      if (data.type === 'consultation') setTabConsultation(sid, null);
      // Opus responses and consultation results are full AI responses — render as assistant with markdown
      const isOpusResponse = data.model === 'opus';
      const isConsultation = data.type === 'consultation';
      const isStatus = data.type === 'status';
      const isRichResponse = isOpusResponse || isConsultation;
      const newMsg: ChatMessage = {
        role: (isConsultation ? 'consultation' : isOpusResponse ? 'assistant' : 'system-action') as ChatMessage['role'],
        text: isRichResponse ? data.text : `⚡ ${data.text}`,
        model: isConsultation ? (data.model || data.provider) : undefined,
        provider: isConsultation ? (data.provider || (isOpusResponse ? 'opus' : undefined)) : undefined,
        timestamp: new Date().toISOString(),
        ...(isConsultation && data.inputTokens != null ? { inputTokens: data.inputTokens } : {}),
        ...(isConsultation && data.outputTokens != null ? { outputTokens: data.outputTokens } : {}),
        ...(isConsultation && data.costUsd != null ? { costUsd: data.costUsd } : {}),
      };
      setTabMessages(sid, (prev) => {
        // Status messages replace previous status (e.g. video progress updates)
        if (isStatus) {
          const lastIdx = prev.length - 1;
          if (lastIdx >= 0 && prev[lastIdx].role === 'system-action' && prev[lastIdx].text?.startsWith('⚡')) {
            return [...prev.slice(0, lastIdx), newMsg];
          }
        }
        // Consultation results replace the preceding status message
        if (isConsultation) {
          const lastIdx = prev.length - 1;
          if (lastIdx >= 0 && prev[lastIdx].role === 'system-action' && prev[lastIdx].text?.startsWith('⚡')) {
            return [...prev.slice(0, lastIdx), newMsg];
          }
        }
        return [...prev, newMsg];
      });
      // Terminal actions (consultations, cheap tasks, session-mgmt) clear busy state.
      // These don't go through the normal CLAUDE_DONE path so they must clear it here.
      if (isConsultation || data.terminal) {
        if (sid) useComposerStore.getState().onStreamDone(sid);
      }
    });

    socket.on(WsEvent.BROWSER_CONSENT_REQUIRED, (data: { sessionId: string; version: string }) => {
      browserConsentRequiredRef.current?.(data);
    });

    socket.on(WsEvent.BROWSER_TIER_GATE, (data: { sessionId: string; reason: string }) => {
      browserTierGateRequiredRef.current?.(data);
    });

    socket.on(WsEvent.FEATURE_GATED, (data: { featureKey: string; reason?: string; upgradeTo?: string; sessionId?: string; clientMsgId?: string }) => {
      if (data.sessionId && data.clientMsgId) {
        useComposerStore.getState().markGated(data.sessionId, data.clientMsgId);
      }
      if (data.featureKey === 'ai.cascade_routing') {
        // Direct named-handler call (R-A2) — replaces the old `cascade-gated` CustomEvent
        // bus (platform-isolation #1657 Phase 2, Slice A). The shell's handler pushes the
        // sessions-drawer history entry + calls the openForCascadeGate() store action.
        cascadeGatedRef.current?.();
      }
      // Stage-5 balance denial (valko-credits-platform Phase 2): the store owns the
      // reason-branch — it opens the top-up modal ⇔ reason === 'insufficient_balance'
      // and is a strict no-op for every other reason, so the cascade upgrade-drawer
      // and composer markGated paths above are unaffected. Named action, R-A2.
      useCreditsStore.getState().onFeatureGated(data);
    });

    socket.on(WsEvent.CREDIT_BALANCE, (data: CreditBalancePayload) => {
      // store (authoritative): the named reconcile action is the ONLY balance writer —
      // the server snapshot always overwrites persisted/local wallet values (R-A2).
      useCreditsStore.getState().onCreditBalance(data);
    });

    // Agent Runs (agent-runs-visualization.md Phase 2): the ONE WORKFLOW_PROGRESS event
    // carries a discriminated `kind` union; the store's single exhaustive reducer routes
    // each kind to its named action (R-A2 — direct call, no CustomEvent bus; R-A5 — the
    // reducer is exhaustive over WORKFLOW_PROGRESS_KINDS). No UI consumes it until Phase 3;
    // wiring it here keeps the store fed the moment the backend (Phase 1) emits.
    socket.on(WsEvent.WORKFLOW_PROGRESS, (data: WorkflowProgressEvent) => {
      useWorkflowProgressStore.getState().applyWireEvent(data);
    });

    // Agent Runs self-heal, push half: the store bumps `pendingSnapshotRequests` on any
    // orphan-gated event (unknown/stale runId — it NEVER invents a run); THIS hook owns the
    // wire (R-A2 — the store stays wire-free), so drain the counter by asking the server for
    // authority and clearing it. clearSnapshotRequests() re-fires the subscriber with 0 → no-op.
    const unsubscribeWorkflowSnapshotHeal = useWorkflowProgressStore.subscribe((s) => {
      if (s.pendingSnapshotRequests > 0 && socket.connected) {
        socket.emit(WsEvent.WORKFLOW_REQUEST_SNAPSHOT, {});
        useWorkflowProgressStore.getState().clearSnapshotRequests();
      }
    });

    socket.on(WsEvent.SWITCH_TO_SESSION, (data: { sessionId: string }) => {
      // Server says switch to this session (voice command: "switch to calendar")
      const sid = data.sessionId;
      setOpenTabs((prev) => {
        if (prev.includes(sid)) return prev;
        const tabs = [sid, ...prev].slice(0, 5);
        saveOpenTabs(tabs);
        return tabs;
      });
      activeTabIdRef.current = sid;
      saveActiveTab(sid);
      setAndPersistCurrentSessionId(sid);
      const tab = tabCache.current.get(sid);
      if (tab && tab.messages.length > 0) {
        syncTabToState(sid);
      } else {
        syncTabToState(sid);
        socket.emit(WsEvent.SWITCH_SESSION, { sessionId: sid });
      }
      autoSwitchRef.current?.();
    });

    socket.on(WsEvent.NEW_SESSION_CREATED, (data: { session: any; autoSwitch?: boolean }) => {
      const s = data.session;
      useSessionListStore.getState().upsert(s);
      if (data.autoSwitch && s.id) {
        setOpenTabs((prev) => {
          const tabs = [s.id, ...prev].slice(0, 5);
          saveOpenTabs(tabs);
          return tabs;
        });
        activeTabIdRef.current = s.id;
        saveActiveTab(s.id);
        setAndPersistCurrentSessionId(s.id);
        syncTabToState(s.id);
        autoSwitchRef.current?.();
      }
    });

    socket.on(WsEvent.MESSAGE_SHARED, (data: { sessionId: string; from: string; preview?: string }) => {
      // Someone shared a response to this user — show notification in target session
      const sid = data.sessionId;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'shared' as ChatMessage['role'], text: `📨 ${data.from} shared a response. Check session for details.`, timestamp: new Date().toISOString() },
      ]);
    });

    // SSH remote session events.
    // ssh-terminal-fsm-migration (Phases 1–5, COMPLETE): the origin-keyed
    // sshTerminalStore is the SINGLE source of truth — the legacy useState/useRef
    // SSH state was deleted in Phase 2. Each handler below dispatches exactly one
    // named store action (the FSM transition); components are pure renderers of
    // store selectors (useSshMode / useSshOutput / useSshTarget / useSshOutputEpoch
    // …). The store's `output` holds only real PTY chunks (P4: idempotent on `seq`);
    // reconnect goes through the server-authoritative SSH_STATE → reconcile (P3),
    // never a frontend timeout. See voice-assistant/docs/ssh-terminal.md § FSM.
    socket.on(WsEvent.SSH_TARGETS, (data: { targets: SshTarget[] }) => {
      const targets = data.targets || [];
      sshTargetsRef.current = targets;
      sshStoreSetTargets(targets); // store (authoritative): populate the picker
    });

    socket.on(WsEvent.SSH_CONNECTED, (data: { target: SshTarget }) => {
      // store (authoritative): connecting → connected (atomic). The green connect
      // banner is presentational and dropped in Phase 2 — the store holds real PTY
      // chunks only; the shell prompt streams in immediately after.
      sshStoreCommitConnect(SSH_PRIMARY_TERMINAL_ID, data.target);
    });

    socket.on(WsEvent.SSH_OUTPUT, (data: SshOutputPayload) => {
      // store: connected self-loop. Phase 4 — idempotent on the optional `seq`
      // cursor so reattach replay + racing live chunks dedupe deterministically.
      sshStoreAppendOutput(SSH_PRIMARY_TERMINAL_ID, data.data, data.seq);
    });

    // ssh-terminal-fsm Phase 3: server-authoritative reconcile on the handshake.
    // The ONLY truth signal that resolves a `detached` terminal — never a timeout.
    //   attached:true  → detached → connected; `replay` re-sources the scrollback
    //                    from the server PTY buffer (the lost-history fix: reattach
    //                    no longer routes through SSH_CONNECTED→commitConnect→clear).
    //   attached:false → detached → idle (no-op when already idle, so a routine
    //                    "no terminal" handshake doesn't churn the store).
    socket.on(WsEvent.SSH_STATE, (data: SshStatePayload) => {
      if (!data.attached) {
        const existing = useSshTerminalStore.getState().terminals.get(SSH_PRIMARY_TERMINAL_ID);
        if (!existing || existing.mode === 'idle') return; // nothing to reconcile
      }
      sshStoreReconcile({
        terminalId: SSH_PRIMARY_TERMINAL_ID,
        attached: data.attached,
        target: data.target,
        replay: data.replay,
        seq: data.seq, // adopt the replay's high-water cursor (Phase 4)
      });
    });

    socket.on(WsEvent.SSH_DISCONNECTED, () => {
      sshStoreOnDisconnected(SSH_PRIMARY_TERMINAL_ID); // store: → idle (entry cleared)
      // Navigate away from the terminal view so the user isn't stuck on a dead screen
      sshDisconnectedRef.current?.();
    });

    socket.on(WsEvent.SESSION_HEALTH_DATA, (data: {
      sessionId?: string;
      fileSize?: number;
      lastModified?: number;
      createdAt?: number;
      inputTokens?: number;
      contextWindow?: number;
      contextFullness?: ContextFullness;
      modelsUsed?: { model: string; count: number }[];
      toolCallCount?: number;
      docsUpdateStats?: { count: number; lastAt?: number };
      autoDocsUpdateThreshold?: number;
      autoDocsPaused?: boolean;
    }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      // Build a partial update — only include fields that are defined in the payload
      // so that minimal emits (e.g. just {autoDocsPaused}) don't overwrite existing data.
      const update: Partial<NonNullable<TabState['sessionHealth']>> = {};
      if (data.fileSize !== undefined) update.fileSize = data.fileSize;
      if (data.lastModified !== undefined) update.lastModified = data.lastModified;
      if (data.createdAt !== undefined) update.createdAt = data.createdAt;
      if (data.inputTokens !== undefined) update.inputTokens = data.inputTokens;
      if (data.contextWindow !== undefined) update.contextWindow = data.contextWindow;
      if (data.modelsUsed !== undefined) update.modelsUsed = data.modelsUsed;
      if (data.toolCallCount !== undefined) update.toolCallCount = data.toolCallCount;
      if (data.docsUpdateStats !== undefined) update.docsUpdateStats = data.docsUpdateStats;
      if (data.autoDocsUpdateThreshold !== undefined) {
        update.autoDocsUpdateThreshold = data.autoDocsUpdateThreshold;
        setUserAutoDocsThreshold(data.autoDocsUpdateThreshold); // keep user-level value in sync
      }
      if (data.autoDocsPaused !== undefined) update.autoDocsPaused = data.autoDocsPaused;
      update.lastHealthAt = Date.now();
      setTabSessionHealth(sid, update);
      // Occupancy union (context-fullness Phase 0b) — the server-computed truth;
      // the client NEVER derives a pct from inputTokens/contextWindow (SE-09).
      if (sid && data.contextFullness) reconcileFromTurn(sid, data.contextFullness);
    });

    // Redis Stream replay
    socket.on(WsEvent.EVENTS_REPLAYED, (data: { events: Array<{ id: string; type: string; data: any }> }) => {
      if (!data.events?.length) return;
      for (const evt of data.events) {
        if (processedEventIds.current.has(evt.id)) continue;
        processedEventIds.current.add(evt.id);
        const listeners = socket.listeners(evt.type);
        for (const fn of listeners) fn(evt.data);
      }
      const lastId = data.events[data.events.length - 1].id;
      lastEventIdRef.current = lastId;
      try { localStorage.setItem('voice-assistant-last-event-id', lastId); } catch {}
      if (processedEventIds.current.size > 3000) {
        const arr = [...processedEventIds.current];
        processedEventIds.current = new Set(arr.slice(-2000));
      }
    });

    // Model catalog updates — invalidate cache and notify UI
    socket.on(WsEvent.MODEL_CATALOG_UPDATED, (data: any) => {
      // Dynamically import to avoid circular deps
      import('../hooks/useModels').then(({ invalidateModelCache }) => {
        invalidateModelCache();
      });
      // Dispatch CustomEvent for any component that wants to show a toast
      window.dispatchEvent(new CustomEvent('model-catalog-updated', { detail: data }));
    });

    // settings-zustand-migration Phase 5: cross-tab sync.
    // When another tab/device for this user mutates settings, the backend emits
    // PREFERENCES_UPDATED to the user's room. Refresh the store via
    // loadFromBackend (debounced 500ms to coalesce bursts when a multi-field
    // patchMemo lands or several settings flip in quick succession).
    socket.on(WsEvent.PREFERENCES_UPDATED, () => {
      if (preferencesReloadTimerRef.current) clearTimeout(preferencesReloadTimerRef.current);
      preferencesReloadTimerRef.current = setTimeout(() => {
        useSettingsStore.getState().loadFromBackend().catch(() => { /* surfaced via _lastError */ });
        preferencesReloadTimerRef.current = null;
      }, 500);
    });

    socket.on(WsEvent.CONTEXT_RESETTING, (data: { sessionId: string; message: string }) => {
      const sid = data.sessionId || activeTabIdRef.current;
      setTabMessages(sid, (prev) => [
        ...prev,
        { role: 'system', text: `⏳ ${data.message}` },
      ]);
      // Gate the input bar while /handoff orchestration runs (manual reset path).
      // CONTEXT_DOCS_UPDATED releases it. Issue #1141.
      if (sid) useComposerStore.getState().startOrchestration(sid);
    });

    socket.on(WsEvent.CONTEXT_RESET, (data: { sessionId: string; resetAt: string }) => {
      // Clear session health so the context bar immediately shows 0% after reset
      const tab = tabCache.current.get(data.sessionId);
      if (tab) {
        tab.sessionHealth = null;
        if (isActiveTab(data.sessionId)) setSessionHealth(null);
      }
      // Occupancy is gone after a reset — the union reads pending, never a stale number.
      clearForSession(data.sessionId);
      // Update the session's contextResetAt in the FSM store
      useSessionListStore.getState().patch(data.sessionId, { contextResetAt: data.resetAt });
      // Reload messages so the context-reset divider marker appears immediately
      socket.emit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: data.sessionId });
    });

    // Plan-pipeline + wave events → the wave/plans FSM store (R-A2, platform-isolation
    // #1657 Phase 2b Slice 2b-waves). Call the named useWaveStore actions DIRECTLY
    // instead of round-tripping through a window CustomEvent — the same pattern as the
    // Phase-2a docTtsStore conversion above. PlansTab + SessionListView read the store
    // (activeWave / awaitingDeploy / plansRevision) instead of `addEventListener`-ing.
    // This drains the LAST two state-transition dispatches off useSocket's event bus
    // (only the permanent view-layer `model-catalog-updated` toast remains).
    const planEvents = [
      WsEvent.CHUNK_STATUS, WsEvent.BATCH_COMPLETE, WsEvent.COST_CAP_REACHED,
      WsEvent.QUEUE_STATE, WsEvent.PHASE_PR_CREATED, WsEvent.PHASE_CI_FAILED,
      WsEvent.PHASE_MERGED, WsEvent.PHASE_COMPLETE,
    ];
    for (const evt of planEvents) {
      socket.on(evt, () => {
        // The former va:plans-changed handler ignored the payload — it only re-fetched.
        useWaveStore.getState().onPlansChanged();
      });
    }

    // Wave execution events → the wave FSM (WaveProgressPanel via PlansTab, plus the
    // SessionListView deploy-gate marker). `{ event, data }` mirrors the former
    // va:wave-event CustomEvent detail shape exactly.
    const waveEvents = [
      WsEvent.WAVE_STARTED, WsEvent.WAVE_ADVANCED, WsEvent.WAVE_COMPLETE, WsEvent.WAVE_FAILED,
      WsEvent.PHASE_DEPLOY_REQUIRED, WsEvent.PHASE_DEPLOY_DETECTED,
    ];
    for (const evt of waveEvents) {
      socket.on(evt, (data: any) => {
        useWaveStore.getState().onWaveEvent({ event: evt, data });
      });
    }

    // Lighthouse session message: WAVE_SESSION_MESSAGE is intentionally NOT handled here.
    // The backend (wave-coordinator.service.ts) still emits it, but lighthouse session
    // messages now reach the UI via the normal chat_message path (WaveCoordinator injects
    // assistant rows directly) — the old `va:wave-session-message` CustomEvent had zero
    // consumers. Dead-dispatch removed in platform-isolation #1657 Phase 2 Slice 2-rest-C;
    // the unhandled WS event is harmless.

    // Segment TTS — pull model responses (doc viewer + per-message chat read-aloud).
    // R-A2 (platform-isolation #1657 Phase 2a): call the named docTtsStore actions
    // DIRECTLY instead of round-tripping through a window CustomEvent. useSocket already
    // imports useDocTtsStore (see setEmit wiring above), so this is a same-module store
    // call — the established pattern for every other socket.on handler here.
    socket.on(WsEvent.TTS_SEGMENT_AUDIO, (data: { index: number; audioBase64: string; durationMs: number }) => {
      useDocTtsStore.getState().onAudioReceived(data);
    });
    socket.on(WsEvent.TTS_SEGMENT_ERROR, (data: { index: number; message: string }) => {
      // Preserve the former bridge's defensive guard: a malformed payload must be a safe
      // no-op (not onSegmentError(undefined) → NaN segment index).
      if (typeof data?.index === 'number') useDocTtsStore.getState().onSegmentError(data.index);
    });

    socketRef.current = socket;
    return () => {
      // Cancel any pending chunk flush
      if (chunkFlushRafRef.current != null) {
        cancelAnimationFrame(chunkFlushRafRef.current);
        chunkFlushRafRef.current = null;
      }
      pendingChunkFlushRef.current.clear();
      unsubscribeCreditsBind();
      unsubscribeWorkflowSnapshotHeal();
      // Remove ALL listeners — we own this socket instance exclusively
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  // ── Actions ────────────────────────────────────────────────────────

  const sendAudio = useCallback(async (chunks: Blob[], images?: ImageAttachment[], originSessionId?: string | null) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;

    const isSsh = getSshMode(useSshTerminalStore.getState(), SSH_PRIMARY_TERMINAL_ID) === 'connected';
    // Origin sid pinned at record-START wins over live activeTabIdRef so
    // swipes between START and STOP (or auto-send-on-silence with no STOP)
    // route the message to the session the operator was on when they started talking.
    const sid = originSessionId ?? activeTabIdRef.current;
    const clientMsgId = crypto.randomUUID();

    if (!isSsh && sid) {      incTurnCounter(sid);
      setTabToolActivities(sid, () => []);
      // Store the clientMsgId so the TRANSCRIPTION handler can call commitSend with the text.
      pendingVoiceClientMsgId.current = { clientMsgId, sessionId: sid };
    }

    if (!isSsh && images && images.length > 0) {
      const thumbnails = images
        .map((img) => img.thumbnailDataUri)
        .filter(Boolean) as string[];
      if (thumbnails.length > 0) {
        pendingImagesRef.current = thumbnails;
      }
    }

    if (chunks.length > 0) {
      // Inherit MIME from the producer (audio/webm;codecs=opus from MediaRecorder
      // path; audio/wav from the AudioWorklet path). Backend sniffs magic bytes
      // anyway so client MIME is informational, but keeping it accurate makes
      // network traces and DB dumps self-describing.
      // Reset server buffer first — guards against leftover incremental chunks
      // from a previously aborted recording.
      socket.emit(WsEvent.AUDIO_RESET);
      const mime = chunks[0]?.type || 'audio/webm;codecs=opus';
      const combined = new Blob(chunks, { type: mime });
      // base64 string payload → plain socket.io EVENT packet, never a BINARY_EVENT
      // (no binary-attachment reassembly desync). See blobToBase64 / incident 2026-06-14.
      const chunkB64 = await blobToBase64(combined);
      socket.emit(WsEvent.AUDIO_CHUNK, { chunkB64 });
    }
    const lang = sttLanguageRef.current;
    const payload: Record<string, unknown> = {};
    if (!isSsh && images && images.length > 0) payload.images = images;
    if (lang) payload.language = lang;
    if (sid) payload.sessionId = sid;
    if (!isSsh && sid) payload.clientMsgId = clientMsgId;
    socket.emit(WsEvent.AUDIO_END, Object.keys(payload).length > 0 ? payload : undefined);

    // 5 s no-ack safety net (voice path — MESSAGE_RECEIVED arrives after Whisper transcription)
    if (!isSsh && sid) {
      const timer = setTimeout(() => {
        noAckTimers.current.delete(clientMsgId);
        pendingVoiceClientMsgId.current = null;
        useComposerStore.getState().markSendFailed(sid, clientMsgId, 'no_ack');
      }, 5000);
      noAckTimers.current.set(clientMsgId, timer);
    }
  }, [connected]);

  /**
   * Stream a single audio chunk to the backend during recording (incremental upload).
   * Returns true if the chunk was actually sent, false if it was dropped (not connected).
   * Callers must treat any false return as a signal to fall back to the combined-blob path.
   * Pass `recordingId` (UUID v4 from start()) + a monotonic `index` so the server stores
   * the chunk in a per-recording staging buffer (survives reconnects) and reassembles in
   * index order. The chunk travels base64-encoded as a STRING (`chunkB64`) — see blobToBase64.
   */
  const sendAudioChunk = useCallback(async (chunk: Blob, recordingId?: string, index?: number): Promise<boolean> => {
    const socket = socketRef.current;
    if (!socket || !connected) return false;
    // base64 string payload → plain socket.io EVENT packet, never a BINARY_EVENT →
    // no binary-attachment reassembly desync (incident 2026-06-14). undefined
    // recordingId/index serialize away (JSON), so legacy callers still work.
    const chunkB64 = await blobToBase64(chunk);
    socket.emit(WsEvent.AUDIO_CHUNK, { recordingId, index, chunkB64 });
    return true;
  }, [connected]);

  /**
   * Send audio for transcription only — returns text via callback, does NOT send to Claude.
   * Pass `alreadyStreamed=true` when chunks were already sent incrementally via sendAudioChunk
   * so this call only sends AUDIO_END (skips the combined AUDIO_CHUNK emit on stop).
   * Pass `recordingId` (UUID v4 minted at start() time) to allow the server to route chunks
   * to a per-recording staging buffer that survives mid-recording WebSocket reconnects.
   */
  const transcribeAudio = useCallback(async (
    chunks: Blob[],
    onResult: (text: string) => void,
    alreadyStreamed?: boolean,
    recordingId?: string,
    originSessionId?: string | null,
  ) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;

    transcribeOnlyCallbackRef.current = onResult;

    // Safety net: if Whisper never responds (timeout, server crash, network drop),
    // auto-clear the "Transcribing…" state after 45 s so the mic is usable again.
    if (transcribeTimeoutRef.current) clearTimeout(transcribeTimeoutRef.current);
    transcribeTimeoutRef.current = setTimeout(() => {
      if (transcribeOnlyCallbackRef.current === onResult) {
        transcribeOnlyCallbackRef.current = null;
        onResult('');
      }
      transcribeTimeoutRef.current = null;
    }, 45_000);

    if (!alreadyStreamed && chunks.length > 0) {
      // Combined-blob fallback (incremental streaming was never used, or was aborted
      // mid-recording — e.g. a socket drop set droppedChunkRef). AUDIO_RESET clears the
      // legacy per-client buffer; this combined blob is the AUTHORITATIVE audio.
      // CRITICAL: do NOT attach recordingId here. The per-recording staging buffer may
      // already hold orphaned chunks 0..N from the aborted incremental stream, and
      // AUDIO_RESET does not clear it — routing the combined blob through the staging
      // path would overwrite index 0 and leave the stale tail, producing a valid-header
      // but corrupt buffer that slips past the framing guard. Sending without recordingId
      // routes the combined blob to the (just-cleared) legacy buffer; the orphaned
      // staging entry is harmlessly evicted by the 5-min sweeper. (Incident 2026-06-14.)
      socket.emit(WsEvent.AUDIO_RESET);
      const mime = chunks[0]?.type || 'audio/webm;codecs=opus';
      const combined = new Blob(chunks, { type: mime });
      const chunkB64 = await blobToBase64(combined);
      socket.emit(WsEvent.AUDIO_CHUNK, { chunkB64 });
    }
    const lang = sttLanguageRef.current;
    const payload: Record<string, unknown> = { transcribeOnly: true };
    if (lang) payload.language = lang;
    // Origin sid pinned at record-START anchors the transcribed text to the
    // session the user was on when they tapped the mic, regardless of swipes.
    const sid = originSessionId ?? activeTabIdRef.current;
    if (sid) payload.sessionId = sid;
    // Use the staging buffer ONLY on the happy incremental path. When we fell back to the
    // combined blob above, the authoritative audio is in the legacy buffer (no recordingId),
    // so AUDIO_END must NOT reference the (now-orphaned) staging entry.
    if (alreadyStreamed && recordingId) payload.recordingId = recordingId;
    socket.emit(WsEvent.AUDIO_END, payload);
  }, [connected]);

  const sendWithAttachments = useCallback((text: string, images: ImageAttachment[], originSessionId?: string | null) => {
    const socket = socketRef.current;
    if (!socket || !connected || images.length === 0) return;

    // PR #1217 — origin-pin parity with sendText. When the caller pinned an
    // origin (record-START session), route the commit there even if the
    // operator swiped tabs during the gesture. Falls back to live activeTab
    // for non-voice paths (image-only attachment send from currently active tab).
    const sid = originSessionId ?? activeTabIdRef.current;
    const clientMsgId = crypto.randomUUID();

    if (sid) {      incTurnCounter(sid);
      setTabToolActivities(sid, () => []);
      useComposerStore.getState().commitSend(sid, { text, attachments: images, clientMsgId });
    }

    const thumbnails = images
      .map((img) => img.thumbnailDataUri)
      .filter(Boolean) as string[];
    setTabMessages(sid, (prev) => [
      ...prev,
      { role: 'user', text, images: thumbnails },
    ]);

    socket.emit(WsEvent.SEND_WITH_ATTACHMENTS, { text, images, sessionId: sid, clientMsgId });

    if (sid) {
      const timer = setTimeout(() => {
        noAckTimers.current.delete(clientMsgId);
        useComposerStore.getState().markSendFailed(sid, clientMsgId, 'no_ack');
      }, 5000);
      noAckTimers.current.set(clientMsgId, timer);
    }
  }, [connected]);

  const newSession = useCallback((model?: string, name?: string, linkedPlanId?: string) => {
    const payload: { model?: string; name?: string; linkedPlanId?: string } = {};
    if (model) payload.model = model;
    if (name) payload.name = name;
    if (linkedPlanId) payload.linkedPlanId = linkedPlanId;
    socketRef.current?.emit(WsEvent.NEW_SESSION, Object.keys(payload).length ? payload : undefined);
    // Clear active tab state
    const oldId = activeTabIdRef.current;
    activeTabIdRef.current = undefined;
    saveActiveTab(undefined);
    setMessages([]);
    setToolActivities([]);
    // Phase 4 (FSM): sessionMeta is derived from sessionListStore — no
    // separate clear needed (the active-tab view follows whatever the store
    // has for the next session).
    setSessionHealth(null);
    setAndPersistCurrentSessionId(null);
    // The next MESSAGES_LOADED will set the new session as active tab
    // Mark that the next MESSAGES_LOADED for an empty session should auto-send pendingPrompt
    pendingNewSession.current = true;
    // sessionCountBeforeCreate must be captured HERE, before the backend's NEW_SESSION
    // reply lands — it's only meaningful as "how many sessions existed prior to this one".
    pendingPersonaGreetingRef.current = shouldGreetPersona({
      sessionCountBeforeCreate: useSessionListStore.getState().sessions.size,
      seen: useCoachStore.getState().seen,
      coachingEnabled: useCoachStore.getState().coachingEnabled,
    });
  }, []);

  // newRelaySession removed (Phase 5): old 'new_relay_session' event is no longer subscribed.
  // Use newSession('agy') or newSession('claude-cli') instead.

  /** Replay "?" (GearMenu/More, §5.4): re-posts the persona intro into the CURRENTLY
   *  active session's thread on demand — does not touch the first-ever-session gate
   *  (resetFirstRunCoaching, called by the caller alongside this, is what re-arms it
   *  for a FUTURE auto-trigger; this call is the immediate, always-available replay). */
  const replayPersonaGreeting = useCallback(() => {
    const sid = activeTabIdRef.current;
    if (!sid) return;
    const sessionName = useSessionListStore.getState().sessions.get(sid)?.name ?? null;
    setTabMessages(sid, (prev) => [
      ...prev,
      { role: 'assistant', text: personaGreetingText(sessionName), timestamp: new Date().toISOString() },
    ]);
  }, []);

  const requestSummary = useCallback((text: string) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;
    setSummarizing(true);
    socket.emit(WsEvent.SUMMARIZE, { text });
  }, [connected]);

  const sendText = useCallback((text: string, originSessionId?: string | null) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;

    // Origin sid pinned at record-START (when called via voice auto-submit)
    // overrides activeTabIdRef, so transcription-arrives-after-swipe routes
    // to the originating session instead of the currently-active one.
    const sid = originSessionId ?? activeTabIdRef.current;
    const clientMsgId = crypto.randomUUID();

    if (sid) {      incTurnCounter(sid);
      setTabToolActivities(sid, () => []);
      // Clear any provider error for this session on new send
      setProviderErrors((prev) => { const next = new Map(prev); next.delete(sid); return next; });
      // Atomic: push submitting entry + clear draft/attachments in one store write
      useComposerStore.getState().commitSend(sid, { text, attachments: [], clientMsgId });
    }
    setTabMessages(sid, (prev) => [...prev, { role: 'user', text, timestamp: new Date().toISOString() }]);
    socket.emit(WsEvent.SEND_TEXT, { text, sessionId: sid, clientMsgId });

    // 5 s no-ack safety net: if MESSAGE_RECEIVED doesn't arrive, mark send_failed.
    // Reconciliation on reconnect overrides this if the server actually got the message.
    if (sid) {
      const timer = setTimeout(() => {
        noAckTimers.current.delete(clientMsgId);
        useComposerStore.getState().markSendFailed(sid, clientMsgId, 'no_ack');
      }, 5000);
      noAckTimers.current.set(clientMsgId, timer);
    }
  }, [connected]);

  /** Re-send a prompt with a one-shot provider override (e.g. "Try with Claude" after Gemini error). */
  const sendTextWithOverride = useCallback((text: string, providerOverride: string) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;

    const sid = activeTabIdRef.current;
    if (sid) {      incTurnCounter(sid);
      setTabToolActivities(sid, () => []);
      setProviderErrors((prev) => { const next = new Map(prev); next.delete(sid); return next; });
    }
    // Do NOT add a new user message — it's already in the chat from the failed attempt
    socket.emit(WsEvent.SEND_TEXT, { text, sessionId: sid, providerOverride });
  }, [connected]);

  /**
   * Operator answered a mid-stream AskUserQuestion (chip tap / inline submit).
   *
   * This is NOT a normal send: there is no held SDK call awaiting a tool_result.
   * The headless SDK already auto-errored the question and ended the turn, so the
   * answer must travel back as a discrete request/response — the backend looks up
   * the stored question, builds a contextualized prose turn, and resumes the
   * session. At the store level the only effect is the single named transition
   * `awaiting_human_answer → streaming` (R-A2: one intent, one named action).
   * No commitSend / clientMsgId / no-ack timer — we are not opening a new outbound
   * entry, we are resuming the existing one.
   */
  const submitAgentAnswer = useCallback((answer: string, originSessionId?: string | null) => {
    const socket = socketRef.current;
    if (!socket || !connected) return;
    const sid = originSessionId ?? activeTabIdRef.current;
    const trimmed = answer?.trim();
    if (!sid || !trimmed) return;
    setTabMessages(sid, (prev) => [...prev, { role: 'user', text: trimmed, timestamp: new Date().toISOString() }]);
    useComposerStore.getState().onHumanAnswerSubmit(sid);
    socket.emit(WsEvent.AGENT_QUESTION_ANSWER, { sessionId: sid, answer: trimmed });
  }, [connected]);

  const clearProviderError = useCallback((sessionId: string) => {
    setProviderErrors((prev) => { const next = new Map(prev); next.delete(sessionId); return next; });
  }, []);

  const listSessions = useCallback(() => {
    socketRef.current?.emit(WsEvent.LIST_SESSIONS);
  }, []);

  const switchSession = useCallback((sessionId: string) => {
    socketRef.current?.emit(WsEvent.SWITCH_SESSION, { sessionId });
  }, []);

  /**
   * Switch to an already-open tab.
   * Thin shim after Phase 1: all logic lives in sessionLoadStore.openSession().
   * openSession() calls wire.setActiveTab() (updates activeTabIdRef immediately
   * to prevent content bleed), wire.syncLegacyTabToState(), and optionally emits
   * SWITCH_SESSION when the session has no cached messages.
   *
   * IMPORTANT (2026-06-01 stuck-skeleton fix): only short-circuit when the
   * sessionLoadStore says this session is already 'loaded'. activeTabIdRef
   * alone is INSUFFICIENT — boot-time LOAD_TAB_MESSAGES can race / fail and
   * leave a session in 'fetching_initial' that still matches the ref; clicking
   * that session from the list view used to silently return here, never
   * re-triggering the load, leaving an infinite skeleton until manual refresh.
   * slsOpenSession is idempotent (hasCachedMessages short-circuits inside),
   * so calling it on an already-loaded session just re-syncs legacy state.
   */
  const switchTab = useCallback((sessionId: string) => {
    if (sessionId === activeTabIdRef.current) {
      const mode = useSessionLoadStore.getState().sessions.get(sessionId)?.mode;
      if (mode === 'loaded') return;
    }
    slsOpenSession(sessionId);
  }, []);

  /** Open a session in a new tab (from session drawer) */
  const openInTab = useCallback((sessionId: string) => {
    setOpenTabs((prev) => {
      if (prev.includes(sessionId)) return prev;
      const limit = options?.maxTabs ?? MAX_TABS;
      if (prev.length >= limit) {
        options?.onTabLimitReached?.(limit);
        return prev;
      }
      const tabs = [sessionId, ...prev];
      saveOpenTabs(tabs);
      return tabs;
    });
    switchTab(sessionId);
  }, [switchTab, options?.maxTabs, options?.onTabLimitReached]);

  /** Close a tab (does NOT delete the session) */
  const closeTab = useCallback((sessionId: string) => {
    tabCache.current.delete(sessionId);
    setOpenTabs((prev) => {
      const tabs = prev.filter((id) => id !== sessionId);
      saveOpenTabs(tabs);

      // If we closed the active tab, switch to another
      if (activeTabIdRef.current === sessionId) {
        const next = tabs[0];
        activeTabIdRef.current = next;
        saveActiveTab(next);
        setAndPersistCurrentSessionId(next ?? null);
        if (next) {
          syncTabToState(next);
        } else {
          setMessages([]);
          setToolActivities([]);
          // sessionMeta derives from sessionListStore (see above).
          setSessionHealth(null);
        }
      }

      return tabs;
    });
  }, [syncTabToState]);

  const renameSession = useCallback((sessionId: string, name: string) => {
    socketRef.current?.emit(WsEvent.RENAME_SESSION, { sessionId, name });
  }, []);

  const deleteSession = useCallback((sessionId: string, docsUpdate?: 'quick' | 'full') => {
    socketRef.current?.emit(WsEvent.DELETE_SESSION, { sessionId, docsUpdate });
  }, []);

  const cancelPrompt = useCallback(() => {
    if (cancelPendingRef.current) return; // deduplicate rapid taps
    cancelPendingRef.current = true;
    const sid = activeTabIdRef.current;
    // ONE intent, ONE named store action (R-A2): withdraw this session's queued prompts locally AND name them to the
    // server. A local-only clear would leave the queue ROW alive — the aborted turn frees the slot, the drain
    // dispatches the very prompt the operator just cancelled — and before this a `queued` entry survived Cancel
    // entirely (onStreamDone below only ever terminates streaming/awaiting_stream), reading busy forever (#2174).
    const withdrawn = sid ? useComposerStore.getState().commitCancel(sid) : [];
    socketRef.current?.emit(WsEvent.CANCEL_PROMPT, buildCancelPromptPayload(sid, withdrawn));
    // Safety timeout: if server doesn't respond within 5s, force-clear busy state
    setTimeout(() => {
      cancelPendingRef.current = false;
      if (sid) {
        useComposerStore.getState().onStreamDone(sid);
      }
    }, 5000);
  }, []);

  const fetchSessionHealth = useCallback(() => {
    socketRef.current?.emit(WsEvent.SESSION_HEALTH);
  }, []);

  // Phase 4: keep sttLanguageRef in sync with the store. Subscribe once;
  // the ref is what audio-send callbacks read at fire time (avoids stale closures).
  useEffect(() => {
    const unsub = useSettingsStore.subscribe((state, prev) => {
      if (state.sttLanguage !== prev.sttLanguage) {
        sttLanguageRef.current = state.sttLanguage;
      }
    });
    return unsub;
  }, []);

  const fetchSshTargets = useCallback(() => {
    socketRef.current?.emit(WsEvent.SSH_TARGETS);
  }, []);

  const sshConnect = useCallback((targetId: string) => {
    // store (authoritative): → connecting. Resolve the full target from the last
    // SSH_TARGETS payload so the store can label the connecting/connected UI.
    // commitConnect (on SSH_CONNECTED) clears any stale output.
    const target = sshTargetsRef.current.find((t) => t.id === targetId);
    if (target) sshStoreSelectTarget(SSH_PRIMARY_TERMINAL_ID, target);
    socketRef.current?.emit(WsEvent.SSH_CONNECT, { targetId });
  }, []);

  const sshDisconnect = useCallback(() => {
    socketRef.current?.emit(WsEvent.SSH_DISCONNECT);
  }, []);

  /**
   * Atomic voluntary terminal exit (top-bar back arrow). One intent = one action:
   * the store transitions connected → disconnecting, which immediately clears the
   * `sshConnected` surface gate (no stranding — the terminal stops being the active
   * surface right away, so opening another chat renders chat, not a stale terminal),
   * then the wire emits SSH_DISCONNECT. SSH_DISCONNECTED later drives → idle.
   * ssh-terminal-fsm-migration Phase 2 — promotes the PR #1371 back-arrow band-aid.
   */
  const sshExitTerminal = useCallback(() => {
    sshStoreExitTerminal(SSH_PRIMARY_TERMINAL_ID);
    socketRef.current?.emit(WsEvent.SSH_DISCONNECT);
  }, []);

  const sshSendInput = useCallback((data: string) => {
    socketRef.current?.emit(WsEvent.SSH_INPUT, { data });
  }, []);

  const sshResize = useCallback((cols: number, rows: number) => {
    socketRef.current?.emit(WsEvent.SSH_RESIZE, { cols, rows });
  }, []);

  const requestSshSummary = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || !connected) return;
    const recent = (useSshTerminalStore.getState().terminals.get(SSH_PRIMARY_TERMINAL_ID)?.output ?? [])
      .slice(-50).join('');
    if (!recent.trim()) return;
    setSummarizing(true);
    socket.emit(WsEvent.SUMMARIZE, { text: recent });
  }, [connected]);

  // inputbar-text-shadow-migration Phase 2: `updateInputDraft` deleted —
  // InputBar now writes drafts directly via `composerStore.setDraft` (no
  // longer routed through useSocket).

  /** Clear visible chat messages for the active tab (visual only — session/context untouched).
   *  Keeps the last user message + any agent replies that followed it. */
  const clearChat = useCallback(() => {
    const sid = activeTabIdRef.current;
    if (!sid) return;
    setTabMessages(sid, (prev) => {
      // Find the last user message and keep it + everything after it
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === 'user') { lastUserIdx = i; break; }
      }
      return lastUserIdx >= 0 ? prev.slice(lastUserIdx) : prev;
    });
    setTabHasMore(sid, true); // allow loading older messages back via scroll-up
  }, []);

  /** Request older messages for the active tab (cursor-based pagination) */
  const loadOlderMessages = useCallback(() => {
    const sid = activeTabIdRef.current;
    if (!sid) return;
    const tab = tabCache.current.get(sid);
    if (!tab || !tab.hasMore) return;
    // Find the oldest message with an id to use as cursor
    const firstWithId = tab.messages.find((m) => m.id);
    if (!firstWithId?.id) {
      // No messages (e.g. chat was cleared) — reload latest via LOAD_TAB_MESSAGES
      slsStartFetchingInitial(sid);
      socketRef.current?.emit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: sid });
      return;
    }
    slsBeginLoadOlder(sid);
    socketRef.current?.emit(WsEvent.LOAD_OLDER_MESSAGES, {
      sessionId: sid,
      before: firstWithId.id,
    });
  }, []);

  /** Send OPUS_CONSULT — user confirmed consultation with selected message IDs and target provider */
  const emitOpusConsult = useCallback((sessionId: string, prompt: string, messageIds: string[], provider = 'opus') => {
    socketRef.current?.emit(WsEvent.OPUS_CONSULT, { sessionId, prompt, messageIds, provider });
  }, []);

  /** Send OPUS_DISMISSED — user dismissed the classifier suggestion, releasing prompt to Sonnet */
  const emitOpusDismissed = useCallback((sessionId: string, prompt: string) => {
    socketRef.current?.emit(WsEvent.OPUS_DISMISSED, { sessionId, prompt });
  }, []);

  return {
    connected, deployStatus, deployPending, lastDeploy, messages, summarizing,
    sessions, currentSessionId, toolActivities, inflightToolRowId, sessionMeta, usage,
    sessionHealth, fetchSessionHealth, userAutoDocsThreshold,
    sendAudio, sendAudioChunk, transcribeAudio, sendWithAttachments, newSession, requestSummary,
    replayPersonaGreeting,
    sendText, listSessions, switchSession, renameSession, deleteSession,
    submitAgentAnswer,
    cancelPrompt,
    // Multi-session tabs
    openTabs, switchTab, openInTab, closeTab,
    // Pagination
    hasMore, loadOlderMessages, clearChat,
    // Session-list sync boundary (desktop-home rail loading state, issue #2201)
    sessionsSynced,
    // SSH
    sshConnected, sshTarget, sshOutput, sshOutputEpoch, sshTargets, sshVoiceHistory,
    fetchSshTargets, sshConnect, sshDisconnect, sshExitTerminal, sshSendInput, sshResize,
    requestSshSummary,
    // Provider failover
    providerInfo,
    providerErrors,
    sendTextWithOverride,
    clearProviderError,
    socketRef,
    // Opus consultation
    emitOpusConsult, emitOpusDismissed,
    // Live cross-model consultation indicator
    activeConsultation,
    // Auto docs-update pause toggle (session-scoped)
    toggleAutoDocsPaused: (paused: boolean) => {
      socketRef.current?.emit(WsEvent.TOGGLE_AUTO_DOCS, { paused });
    },
    // Auto docs-update threshold (user preference, persisted via PUT /settings/memo)
    updateAutoDocsThreshold: (threshold: number) => {
      setUserAutoDocsThreshold(threshold); // immediate update across all sessions
      fetch('/api/settings/memo', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoDocsUpdateThreshold: threshold }),
      }).catch(() => {});
    },
    // Project pin/unpin (active project detection)
    pinProject: (sessionId: string, project: string) => {
      socketRef.current?.emit(WsEvent.PIN_PROJECT, { sessionId, project });
      const existing = useSessionListStore.getState().sessions.get(sessionId);
      const newActive = [project, ...((existing?.activeProjects || []).filter((k) => k !== project))].slice(0, 3);
      useSessionListStore.getState().patch(sessionId, { pinnedProject: project, activeProjects: newActive });
    },
    unpinProject: (sessionId: string) => {
      socketRef.current?.emit(WsEvent.UNPIN_PROJECT, { sessionId });
      useSessionListStore.getState().patch(sessionId, { pinnedProject: null });
    },
    pinSessionToList: (sessionId: string) => {
      socketRef.current?.emit(WsEvent.PIN_SESSION_TO_LIST, { sessionId });
      // Optimistic update: assign max+1 position
      const allEntries = Array.from(useSessionListStore.getState().sessions.values());
      const maxPos = Math.max(0, ...allEntries.map((s) => s.listPinPosition ?? 0));
      useSessionListStore.getState().patch(sessionId, { listPinPosition: maxPos + 1 });
    },
    unpinSessionFromList: (sessionId: string) => {
      socketRef.current?.emit(WsEvent.UNPIN_SESSION_FROM_LIST, { sessionId });
      // Optimistic update
      useSessionListStore.getState().patch(sessionId, { listPinPosition: null });
    },
    resetContext: (sessionId: string, saveKnowledge?: boolean) => {
      if (saveKnowledge) useComposerStore.getState().startOrchestration(sessionId);
      socketRef.current?.emit(WsEvent.RESET_CONTEXT, { sessionId, saveKnowledge });
    },
    /** Patch effort / 1M-context / model for the session. Optimistic local update + PATCH /api/sessions/:id/tuning. */
    updateSessionTuning: async (
      sessionId: string,
      patch: { effort?: EffortLevel | null; enable1mContext?: boolean; model?: string },
    ) => {
      // Optimistic
      const optimisticPatch: Partial<SessionEntry> = {};
      if (patch.effort !== undefined) optimisticPatch.effort = patch.effort;
      if (patch.enable1mContext !== undefined) optimisticPatch.enable1mContext = patch.enable1mContext;
      if (patch.model !== undefined) optimisticPatch.model = patch.model;
      if (Object.keys(optimisticPatch).length > 0) useSessionListStore.getState().patch(sessionId, optimisticPatch);
      try {
        const res = await patchSessionTuning(sessionId, patch);
        // Reconcile with server response (handles capability rejection edge cases)
        const reconcilePatch: Partial<SessionEntry> = {
          effort: res.effort,
          enable1mContext: res.enable1mContext,
        };
        if (res.model !== undefined) reconcilePatch.model = res.model;
        useSessionListStore.getState().patch(sessionId, reconcilePatch);
      } catch (err) {
        console.error('[updateSessionTuning] failed', err);
        // Best-effort: leave optimistic state; user can retry
      }
    },
    /** Set a prompt to auto-send when the next new session becomes active */
    setPendingPrompt: (prompt: string | null) => {
      pendingPromptRef.current = prompt;
    },
  };
}
